import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import SearchIcon from '@mui/icons-material/Search';
import StorageIcon from '@mui/icons-material/Storage';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import DeduplicateIcon from '@mui/icons-material/FilterNone';
import SortIcon from '@mui/icons-material/Sort';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import JoinInnerIcon from '@mui/icons-material/JoinInner';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Plot from 'react-plotly.js';
import { autoFormat, detectColumnType, formatCell, formatCompact, formatInteger } from '../utils/formatters';

// ─── Stats helpers ────────────────────────────────────────────────────────────
/** Humanize a column name: "daily_screen_time_hours" → "Daily Screen Time Hours" */
function humanize(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Pearson correlation coefficient */
function pearsonR(xs: number[], ys: number[]): number {
  const n = Math.min(xs.length, ys.length);
  if (n < 3) return 0;
  let sumX = 0, sumY = 0;
  for (let i = 0; i < n; i++) { sumX += xs[i]; sumY += ys[i]; }
  const meanX = sumX / n, meanY = sumY / n;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX, dy = ys[i] - meanY;
    num += dx * dy; denX += dx * dx; denY += dy * dy;
  }
  if (denX === 0 || denY === 0) return 0;
  return num / Math.sqrt(denX * denY);
}

/** Standard normal CDF (Abramowitz & Stegun approximation) */
function normCdf(x: number): number {
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}

/** Two-tailed p-value for Pearson correlation (t-distribution via normal approximation) */
function correlationPValue(r: number, n: number): number {
  if (!isFinite(r) || Math.abs(r) >= 1 || n <= 2) return 1;
  const t = r * Math.sqrt((n - 2) / (1 - r * r));
  return Math.max(0, Math.min(1, 2 * (1 - normCdf(Math.abs(t)))));
}

/** Format p-value for display: "<0.001", "0.012", "0.42" */
function formatPValue(p: number): string {
  if (p < 0.001) return '<0.001';
  if (p < 0.01) return p.toFixed(3);
  return p.toFixed(2);
}

/** Strength label for |r| */
function correlationStrength(r: number): string {
  const a = Math.abs(r);
  if (a >= 0.7) return 'Strong';
  if (a >= 0.4) return 'Moderate';
  if (a >= 0.2) return 'Weak';
  return 'Very weak';
}

/** Percentile (linear interpolation) */
function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx), hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] * (hi - idx) + sorted[hi] * (idx - lo);
}

/**
 * Detect whether a column is an identifier (primary key / foreign key-like).
 * Identifier columns are useless for analysis — can't aggregate, group, correlate, or filter meaningfully.
 * Rules (any one):
 *   1. Name pattern matches id/uuid/guid/key/serial/hash/code
 *   2. unique_count equals total_rows (every value unique)
 *   3. unique_count / total_rows > 0.9 AND unique_count > 100 (near-unique high-cardinality)
 */
const ID_NAME_PATTERN = /(^|_)id$|^id_|^id$|uuid|guid|_key$|^key_|^key$|serial(_?number)?|hash|^code$|_code$/i;

function isIdentifierColumn(col: { column_name: string; unique_count?: number }, totalRows: number): boolean {
  if (ID_NAME_PATTERN.test(col.column_name)) return true;
  if (col.unique_count != null && totalRows > 0) {
    if (col.unique_count === totalRows) return true;
    const uniqueRatio = col.unique_count / totalRows;
    if (uniqueRatio > 0.9 && col.unique_count > 100) return true;
  }
  return false;
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface Project { id: number; name: string; }
interface Dataset { id: number; name: string; table_name: string; project_id: number; }

interface Props {
  project: Project;
  dataset: Dataset;
  onBack: () => void;
}

type Tab = 'explore' | 'filter' | 'add' | 'chart' | 'ai' | 'columns' | 'wrangle';

interface ColumnInfo {
  column_name: string;
  data_type: 'numeric' | 'categorical';
  min_value?: number;
  max_value?: number;
  avg_value?: number;
  unique_count?: number;
  top_values?: { value: string; frequency: number; percentage: number }[];
  sample_values?: any[];
}

interface Overview {
  columns: string[];
  total_rows: number;
  total_columns: number;
  data_types: { numeric: number; categorical: number; datetime: number };
  missing_values: Record<string, number>;
  sample_data: Record<string, any>[];
  numerical_column_names: string[];
  categorical_column_names: string[];
}

interface Filter {
  column_name: string;
  filter_type: 'range' | 'equals' | 'includes' | 'excludes' | 'contains' | 'starts_with' | 'ends_with';
  values: any;
}

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  data?: any[];
  sql?: string;
  insights?: string[];
  loading?: boolean;
}

// ─── Palette (pastel, approachable) ───────────────────────────────────────────
const PALETTE = ['#A8D5BA', '#A5C8F4', '#F4A5A5', '#C5B4E3', '#F9D89C', '#B8E0D2', '#FFB5B5', '#D6B3E0', '#FFE5A0', '#B8D8E8'];

// ─── Tabs config ──────────────────────────────────────────────────────────────
const TABS: { id: Tab; label: string; icon: React.ReactNode; help: string }[] = [
  { id: 'explore', label: 'Overview', icon: <StorageIcon sx={{ fontSize: 16 }} />, help: 'See your data at a glance' },
  { id: 'filter',  label: 'Filter',   icon: <FilterAltIcon sx={{ fontSize: 16 }} />, help: 'Narrow down to what matters' },
  { id: 'add',     label: 'Add Column', icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />, help: 'Create new columns from existing ones' },
  { id: 'chart',   label: 'Chart',    icon: <BarChartIcon sx={{ fontSize: 16 }} />, help: 'Visualize your data' },
  { id: 'ai',      label: 'Ask AI',   icon: <AutoAwesomeIcon sx={{ fontSize: 16 }} />, help: 'Ask questions in plain English' },
  { id: 'columns', label: 'Columns', icon: <VisibilityOffIcon sx={{ fontSize: 16 }} />, help: 'Choose which columns to include or ignore' },
  { id: 'wrangle', label: 'Wrangle', icon: <MergeTypeIcon sx={{ fontSize: 16 }} />, help: 'Join, deduplicate, sort, and extract unique values — Excel-style' },
];

// ─── Small UI atoms ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ title: string; subtitle?: string; right?: React.ReactNode }> = ({ title, subtitle, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 2 }}>
    <Box>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#111', letterSpacing: '-0.01em' }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {right}
  </Box>
);

const KpiCard: React.FC<{ label: string; value: string; hint?: string; color?: string; icon?: React.ReactNode }> = ({ label, value, hint, color = '#3B82F6', icon }) => (
  <Box sx={{ flex: '1 1 180px', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, bgcolor: '#fff', display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    {icon && (
      <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color }}>
        {icon}
      </Box>
    )}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#111', lineHeight: 1.1, letterSpacing: '-0.02em' }} noWrap>{value}</Typography>
      {hint && <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.25 }}>{hint}</Typography>}
    </Box>
  </Box>
);

// ─── Recipe data computation (used by widgets) ────────────────────────────────
type WidgetChartType = 'bar' | 'line' | 'area' | 'pie' | 'scatter';
interface RecipeData {
  labels: string[];
  values: number[];
  xs?: number[];
  ys?: number[]; // for scatter
}

function computeRecipeData(
  rows: Record<string, any>[],
  xCol: string,
  yCol: string,
  agg: 'sum' | 'mean' | 'median' | 'count',
  chartType: WidgetChartType,
  maxCategories = 12
): RecipeData {
  if (chartType === 'scatter') {
    const xs: number[] = [], ys: number[] = [];
    for (const r of rows) {
      const xv = Number(r[xCol]), yv = Number(r[yCol]);
      if (!isNaN(xv) && !isNaN(yv) && r[xCol] != null && r[yCol] != null) {
        xs.push(xv); ys.push(yv);
      }
    }
    return { labels: [], values: [], xs, ys };
  }
  const groups: Record<string, number[]> = {};
  for (const r of rows) {
    const key = String(r[xCol] ?? '—');
    const val = Number(r[yCol]);
    if (!groups[key]) groups[key] = [];
    if (!isNaN(val)) groups[key].push(val);
  }
  const aggFn = (vs: number[]) => {
    if (vs.length === 0) return 0;
    if (agg === 'sum') return vs.reduce((a, b) => a + b, 0);
    if (agg === 'mean') return vs.reduce((a, b) => a + b, 0) / vs.length;
    if (agg === 'median') { const s = [...vs].sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; }
    if (agg === 'count') return vs.length;
    return 0;
  };
  let entries = Object.entries(groups).map(([k, v]) => [k, aggFn(v)] as [string, number]);
  if (chartType === 'line' || chartType === 'area') {
    entries.sort(([a], [b]) => {
      const da = Date.parse(a), db = Date.parse(b);
      if (!isNaN(da) && !isNaN(db)) return da - db;
      const na = Number(a), nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return String(a).localeCompare(String(b));
    });
  } else {
    entries.sort((a, b) => b[1] - a[1]);
  }
  const cap = chartType === 'pie' ? 8 : maxCategories;
  if (entries.length > cap) entries = entries.slice(0, cap);
  return { labels: entries.map(e => e[0]), values: entries.map(e => e[1]) };
}

// ─── Dashboard Widget (a live, downloadable mini chart) ───────────────────────
interface WidgetRecipe {
  id: string;
  title: string;
  subtitle: string;
  insightTemplate: string; // will be filled with computed stats below
  xColumn: string;
  yColumn: string;
  agg: 'sum' | 'mean' | 'median' | 'count';
  chartType: WidgetChartType;
  correlation?: { r: number; p: number; n: number };
}

const PALETTE_WIDGET = ['#A8D5BA', '#A5C8F4', '#F4A5A5', '#C5B4E3', '#F9D89C', '#B8E0D2', '#FFB5B5', '#D6B3E0', '#FFE5A0', '#B8D8E8'];

const DashboardWidget: React.FC<{
  recipe: WidgetRecipe;
  rows: Record<string, any>[];
  datasetName: string;
  loadedAt: Date;
  onOpenInChart: (x: string, y: string, agg: any, type: WidgetChartType) => void;
}> = ({ recipe, rows, datasetName, loadedAt, onOpenInChart }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const plotDivRef = useRef<any>(null);

  const data = useMemo(() => computeRecipeData(rows, recipe.xColumn, recipe.yColumn, recipe.agg, recipe.chartType), [rows, recipe]);
  const yType = useMemo(() => detectColumnType(recipe.yColumn, rows.slice(0, 20).map(r => r[recipe.yColumn])), [rows, recipe.yColumn]);

  // ── Compute dynamic insight
  const computedInsight = useMemo(() => {
    if (recipe.chartType === 'scatter' && recipe.correlation) {
      const c = recipe.correlation;
      const strength = correlationStrength(c.r).toLowerCase();
      const direction = c.r > 0 ? 'positive' : 'negative';
      const sig = c.p < 0.05 ? ` (p=${formatPValue(c.p)}, statistically significant)` : ` (p=${formatPValue(c.p)}, not significant)`;
      return `${humanize(recipe.xColumn)} and ${humanize(recipe.yColumn)} show a ${strength} ${direction} relationship. r=${c.r.toFixed(2)}${sig}.`;
    }
    if (recipe.chartType === 'pie' && data.values.length > 0) {
      const total = data.values.reduce((a, b) => a + b, 0);
      const top = data.values[0];
      const topLabel = data.labels[0];
      const topPct = total > 0 ? (top / total * 100).toFixed(1) : '0';
      return `${topLabel} is the largest share at ${topPct}% of the total.`;
    }
    if ((recipe.chartType === 'bar') && data.values.length > 0) {
      const total = data.values.reduce((a, b) => a + b, 0);
      const top3 = data.values.slice(0, 3).reduce((a, b) => a + b, 0);
      const pct = total > 0 ? (top3 / total * 100).toFixed(0) : '0';
      const topLabel = data.labels[0];
      return `${topLabel} leads; top 3 account for ${pct}% of total ${humanize(recipe.yColumn)}.`;
    }
    if ((recipe.chartType === 'line' || recipe.chartType === 'area') && data.values.length >= 2) {
      const first = data.values[0];
      const last = data.values[data.values.length - 1];
      if (first === 0) return `${humanize(recipe.yColumn)} changes over ${humanize(recipe.xColumn)}.`;
      const pct = ((last - first) / Math.abs(first) * 100);
      const dir = pct > 5 ? 'rising' : pct < -5 ? 'falling' : 'flat';
      return `${humanize(recipe.yColumn)} is ${dir} over time — ${pct >= 0 ? '+' : ''}${pct.toFixed(1)}% from start to end.`;
    }
    return recipe.insightTemplate;
  }, [recipe, data]);

  const download = () => {
    const Plotly = (window as any).Plotly;
    if (Plotly && plotDivRef.current) {
      Plotly.downloadImage(plotDivRef.current, {
        format: 'png',
        filename: `${datasetName}_${recipe.title}`.replace(/[^a-z0-9_]/gi, '_'),
        width: 1400, height: 700, scale: 2,
      });
    }
    setMenuAnchor(null);
  };

  const freshness = (() => {
    const s = Math.round((Date.now() - loadedAt.getTime()) / 1000);
    if (s < 60) return 'Just now';
    if (s < 3600) return `${Math.round(s / 60)} min ago`;
    if (s < 86400) return `${Math.round(s / 3600)} hours ago`;
    return loadedAt.toLocaleString();
  })();

  // ── Build trace based on chartType
  const trace: any = (() => {
    if (recipe.chartType === 'pie') {
      return {
        type: 'pie', labels: data.labels, values: data.values,
        marker: { colors: PALETTE_WIDGET },
        hole: 0.58,
        textinfo: 'percent',
        textfont: { family: 'Inter, sans-serif', size: 11 },
        hoverinfo: 'label+value+percent',
        sort: false,
      };
    }
    if (recipe.chartType === 'scatter') {
      return {
        type: 'scatter', mode: 'markers', x: data.xs, y: data.ys,
        marker: { color: PALETTE_WIDGET[0], size: 7, opacity: 0.6, line: { color: '#fff', width: 0.5 } },
      };
    }
    if (recipe.chartType === 'line') {
      return {
        type: 'scatter', mode: 'lines+markers', x: data.labels, y: data.values,
        line: { color: PALETTE_WIDGET[1], width: 2.5, shape: 'spline' },
        marker: { color: PALETTE_WIDGET[1], size: 5 },
      };
    }
    if (recipe.chartType === 'area') {
      return {
        type: 'scatter', mode: 'lines', x: data.labels, y: data.values,
        line: { color: PALETTE_WIDGET[2], width: 2, shape: 'spline' },
        fill: 'tozeroy', fillcolor: PALETTE_WIDGET[2] + '55',
      };
    }
    // bar
    return {
      type: 'bar', x: data.labels, y: data.values,
      marker: { color: data.labels.map((_, i) => PALETTE_WIDGET[i % PALETTE_WIDGET.length]), line: { color: '#fff', width: 1.5 } },
    };
  })();

  const hasData = recipe.chartType === 'scatter' ? (data.xs && data.xs.length > 0) : data.values.length > 0;

  return (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #E5E7EB',
      borderRadius: '12px',
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 340,
      transition: 'border-color 0.15s, box-shadow 0.15s',
      '&:hover': { borderColor: '#D1D5DB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111', lineHeight: 1.25 }} noWrap>
            {recipe.title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Chip size="small" label={recipe.chartType}
              sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 600, fontSize: '0.62rem', height: 16, textTransform: 'capitalize' }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF' }} noWrap>{recipe.subtitle}</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={e => setMenuAnchor(e.currentTarget)}
          sx={{ color: '#9CA3AF', '&:hover': { color: '#111', bgcolor: '#F3F4F6' } }}>
          <MoreVertIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 200 }}>
        {hasData ? (
          <Plot
            data={[trace]}
            layout={{
              autosize: true,
              height: 210,
              margin: { l: 50, r: 10, t: 10, b: recipe.chartType === 'pie' ? 10 : 45 },
              font: { family: 'Inter, sans-serif', size: 10, color: '#111' },
              paper_bgcolor: '#fff',
              plot_bgcolor: '#fff',
              showlegend: false,
              xaxis: recipe.chartType !== 'pie' ? {
                title: { text: humanize(recipe.xColumn), font: { size: 10, color: '#6B7280' }, standoff: 8 } as any,
                tickangle: data.labels.length > 6 ? -30 : 0,
                gridcolor: '#F3F4F6', linecolor: '#E5E7EB',
                tickfont: { size: 9, color: '#6B7280' },
                automargin: true,
              } : undefined,
              yaxis: recipe.chartType !== 'pie' ? {
                title: { text: humanize(recipe.yColumn), font: { size: 10, color: '#6B7280' }, standoff: 5 } as any,
                gridcolor: '#F3F4F6', linecolor: '#E5E7EB',
                tickfont: { size: 9, color: '#6B7280' },
                tickformat: yType === 'currency' ? '$,.0s' : yType === 'percent' ? '.1%' : '~s',
                automargin: true,
              } : undefined,
              transition: { duration: 500, easing: 'cubic-in-out' } as any,
            }}
            config={{ displayModeBar: false, responsive: true, staticPlot: false }}
            useResizeHandler
            style={{ width: '100%', height: '100%' }}
            onInitialized={(_, gd) => { plotDivRef.current = gd; }}
            onUpdate={(_, gd) => { plotDivRef.current = gd; }}
          />
        ) : (
          <Box sx={{ height: 210, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>Not enough data to chart.</Typography>
          </Box>
        )}
      </Box>

      {/* Insight */}
      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F3F4F6' }}>
        <Typography sx={{ fontSize: '0.76rem', color: '#374151', lineHeight: 1.5 }}>
          <b style={{ color: '#F59E0B' }}>💡</b> {computedInsight}
        </Typography>
      </Box>

      {/* Menu popover */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 280, borderRadius: '10px', border: '1px solid #E5E7EB', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', mt: 0.5 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>Source</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#111', mb: 1.25, fontWeight: 500 }}>{datasetName}</Typography>

          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>Data Freshness</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#111', mb: 1.25 }}>
            Loaded {freshness} · {formatInteger(rows.length)} rows sampled
          </Typography>

          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>Key Insight</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#111', mb: 1.5, lineHeight: 1.5 }}>{computedInsight}</Typography>

          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.3 }}>Chart Type</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#111', mb: 2, textTransform: 'capitalize' }}>{recipe.chartType} chart · {recipe.agg}</Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} onClick={download}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.82rem', color: '#111', py: 0.75, '&:hover': { bgcolor: '#F9FAFB' } }}>
            Download as PNG
          </Button>
          <Button startIcon={<OpenInFullIcon sx={{ fontSize: 16 }} />}
            onClick={() => { onOpenInChart(recipe.xColumn, recipe.yColumn, recipe.agg, recipe.chartType); setMenuAnchor(null); }}
            sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.82rem', color: '#111', py: 0.75, '&:hover': { bgcolor: '#F9FAFB' } }}>
            Open in Chart tab
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ExplorePage: React.FC<Props> = ({ project, dataset, onBack }) => {
  const [tab, setTab] = useState<Tab>('explore');

  // ── Overview state ─────────────────────────────────────────────
  const [overview, setOverview] = useState<Overview | null>(null);
  const [columnsInfo, setColumnsInfo] = useState<ColumnInfo[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date>(new Date());
  const [ignoredColumns, setIgnoredColumns] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  // ── Filter state ───────────────────────────────────────────────
  const [filterCol, setFilterCol] = useState<string>('');
  const [filterOp, setFilterOp] = useState<'equals' | 'contains' | 'greater_than' | 'less_than' | 'between'>('equals');
  const [filterVal1, setFilterVal1] = useState<string>('');
  const [filterVal2, setFilterVal2] = useState<string>(''); // for between
  const [activeFilters, setActiveFilters] = useState<Filter[]>([]);
  const [filterPreview, setFilterPreview] = useState<{ rows: any[]; count: number } | null>(null);
  const [filterBusy, setFilterBusy] = useState(false);
  const [savingFilter, setSavingFilter] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [saveFilterOpen, setSaveFilterOpen] = useState(false);

  // ── View SQL state ─────────────────────────────────────────────
  const [viewSqlOpen, setViewSqlOpen] = useState(false);
  const [viewSqlText, setViewSqlText] = useState('');
  const [viewSqlBusy, setViewSqlBusy] = useState(false);

  // ── Column analysis state ─────────────────────────────────────
  const [columnAnalysis, setColumnAnalysis] = useState<any>(null);
  const [columnAnalysisCol, setColumnAnalysisCol] = useState<string>('');
  const [columnAnalysisBusy, setColumnAnalysisBusy] = useState(false);
  const [columnAnalysisOpen, setColumnAnalysisOpen] = useState(false);

  // ── Column picker state ───────────────────────────────────────
  const [previewVisibleCols, setPreviewVisibleCols] = useState<Set<string> | null>(null); // null = show all
  const [colPickerAnchor, setColPickerAnchor] = useState<HTMLElement | null>(null);

  // ── Add column state ───────────────────────────────────────────
  const [addName, setAddName] = useState('');
  const [addDesc, setAddDesc] = useState('');
  const [addPreview, setAddPreview] = useState<{ sql: string; explanation: string } | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addResult, setAddResult] = useState<{ success: boolean; message: string } | null>(null);

  // ── Chart state ────────────────────────────────────────────────
  const [chartX, setChartX] = useState<string>('');
  const [chartY, setChartY] = useState<string>('');
  const [chartAgg, setChartAgg] = useState<'sum' | 'mean' | 'median' | 'count'>('sum');
  const [chartType, setChartType] = useState<'bar' | 'line' | 'pie' | 'scatter' | 'area' | 'histogram' | 'waterfall' | 'bubble'>('bar');
  const [chartHidden, setChartHidden] = useState<Set<string>>(new Set()); // for pie "close" animation
  const [chartData, setChartData] = useState<any>(null);
  const [chartBusy, setChartBusy] = useState(false);
  const [chartError, setChartError] = useState<string | null>(null);

  // ── Plot ref (for download) ────────────────────────────────────
  const plotDivRef = useRef<any>(null);

  // ── Wrangle state ──────────────────────────────────────────────
  // Sub-tool: 'join' | 'dedup' | 'duplicates' | 'sort' | 'unique'
  const [wrangleTool, setWrangleTool] = useState<'join' | 'dedup' | 'duplicates' | 'sort' | 'unique'>('join');
  const [wrangleBusy, setWrangleBusy] = useState(false);
  const [wrangleResult, setWrangleResult] = useState<{ success: boolean; message: string; rows?: any[]; rowCount?: number } | null>(null);
  // Join
  const [joinTableB, setJoinTableB] = useState('');
  const [joinColA, setJoinColA] = useState('');
  const [joinColB, setJoinColB] = useState('');
  const [joinType, setJoinType] = useState<'left' | 'inner' | 'right' | 'full'>('left');
  const [joinNewName, setJoinNewName] = useState('');
  const [joinTableBCols, setJoinTableBCols] = useState<string[]>([]);
  const [allDatasets, setAllDatasets] = useState<Array<{ table_name: string; name: string }>>([]);
  // Dedup
  const [dedupCols, setDedupCols] = useState<string[]>([]);
  const [dedupKeep, setDedupKeep] = useState<'first' | 'last' | 'none'>('first');
  const [dedupNewName, setDedupNewName] = useState('');
  // Find duplicates
  const [dupCheckCols, setDupCheckCols] = useState<string[]>([]);
  const [dupRows, setDupRows] = useState<any[]>([]);
  // Sort
  const [sortCols, setSortCols] = useState<Array<{ column: string; direction: 'asc' | 'desc' }>>([{ column: '', direction: 'asc' }]);
  const [sortNewName, setSortNewName] = useState('');
  // Unique values
  const [uniqueCol, setUniqueCol] = useState('');
  const [uniqueRows, setUniqueRows] = useState<any[]>([]);

  // ── AI state ───────────────────────────────────────────────────
  const [aiInput, setAiInput] = useState('');
  const [aiMessages, setAiMessages] = useState<ChatMessage[]>([
    { role: 'ai', text: `Hi! I'm your data assistant. Ask me anything about "${dataset.name}" in plain English — like "What's the top product?" or "Show me monthly trends."` },
  ]);
  const [aiBusy, setAiBusy] = useState(false);
  const aiEndRef = useRef<HTMLDivElement>(null);

  // ── Initial load: overview + columns + preview ───────────────────────
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [ovRes, colsRes, prevRes] = await Promise.all([
          fetch(`/api/tables/${dataset.table_name}/overview`, { credentials: 'include' }),
          fetch(`/api/filter/${dataset.table_name}/columns`, { credentials: 'include' }),
          fetch(`/api/tables/${dataset.table_name}/query?limit=500`, { credentials: 'include' }),
        ]);
        if (!ovRes.ok) throw new Error('Could not load dataset overview');
        const ov: Overview = await ovRes.json();
        setOverview(ov);

        if (colsRes.ok) {
          const data = await colsRes.json();
          setColumnsInfo(data.columns || []);
          // Default selections — skip identifier columns (IDs, UUIDs, hashes)
          if (data.columns?.length > 0) {
            const totalRows = ov?.total_rows || 0;
            const nonId = data.columns.filter((c: ColumnInfo) => !isIdentifierColumn(c, totalRows));
            const pool = nonId.length > 0 ? nonId : data.columns;
            // Prefer a categorical with 2–20 unique values for grouping
            const goodCat = pool.find((c: ColumnInfo) => c.data_type === 'categorical' && c.unique_count != null && c.unique_count >= 2 && c.unique_count <= 20)
              || pool.find((c: ColumnInfo) => c.data_type === 'categorical');
            const firstNum = pool.find((c: ColumnInfo) => c.data_type === 'numeric');
            setFilterCol(goodCat?.column_name || pool[0].column_name);
            setChartX(goodCat?.column_name || pool[0].column_name);
            setChartY(firstNum?.column_name || pool[0].column_name);
          }
        }

        if (prevRes.ok) {
          const rows = await prevRes.json();
          setPreviewRows(rows);
        }
        setLoadedAt(new Date());
      } catch (e: any) {
        setLoadError(e?.message || 'Could not load dataset. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [dataset.table_name]);

  // ── Load sibling datasets for Join picker ───────────────────────
  useEffect(() => {
    if (tab !== 'wrangle') return;
    fetch(`/api/projects/${project.id}/datasets`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((datasets: any[]) => {
        // Flatten parent + filtered datasets, exclude current dataset
        const all: Array<{ table_name: string; name: string }> = [];
        for (const d of datasets) {
          if (d.table_name !== dataset.table_name) all.push({ table_name: d.table_name, name: d.name });
          for (const fd of (d.filtered_datasets || [])) {
            if (fd.table_name !== dataset.table_name) all.push({ table_name: fd.table_name, name: fd.name });
          }
        }
        setAllDatasets(all);
      })
      .catch(() => {});
  }, [tab, project.id, dataset.table_name]);

  // ── Fetch columns for the selected join table B ──────────────────
  useEffect(() => {
    if (!joinTableB) { setJoinTableBCols([]); return; }
    fetch(`/api/filter/${joinTableB}/columns`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { columns: [] })
      .then(d => setJoinTableBCols((d.columns || []).map((c: any) => c.column_name)))
      .catch(() => {});
  }, [joinTableB]);

  // ── Auto-scroll AI chat ─────────────────────────────────────────
  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  // ── Column helpers (respect ignored columns everywhere) ─────────
  const activeColumnsInfo = useMemo(() => columnsInfo.filter(c => !ignoredColumns.has(c.column_name)), [columnsInfo, ignoredColumns]);
  const allColumns = activeColumnsInfo.map(c => c.column_name);
  const numericCols = activeColumnsInfo.filter(c => c.data_type === 'numeric').map(c => c.column_name);
  const categoricalCols = activeColumnsInfo.filter(c => c.data_type === 'categorical').map(c => c.column_name);
  const currentFilterCol = columnsInfo.find(c => c.column_name === filterCol);

  // ── Filtered preview (client-side for search in overview table) ──
  const visiblePreview = useMemo(() => {
    if (!search.trim()) return previewRows;
    const q = search.toLowerCase();
    return previewRows.filter(r =>
      Object.values(r).some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }, [previewRows, search]);

  const pagePreview = visiblePreview.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(visiblePreview.length / PAGE_SIZE));

  // ── Proactive insights for overview ─────────────────────────────
  const insights = useMemo(() => {
    if (!overview) return null;
    const totalCells = overview.total_rows * overview.total_columns;
    const totalMissing = Object.values(overview.missing_values || {}).reduce((s, n) => s + Number(n || 0), 0);
    const completeness = totalCells > 0 ? (1 - totalMissing / totalCells) * 100 : 100;
    return {
      rows: overview.total_rows,
      cols: overview.total_columns,
      numeric: overview.data_types.numeric,
      categorical: overview.data_types.categorical,
      completeness,
      missingCount: totalMissing,
    };
  }, [overview]);

  // ── Smart insights: correlations, outliers, recommendations ─────
  const smartInsights = useMemo(() => {
    if (previewRows.length < 5 || columnsInfo.length === 0 || !overview) return null;

    // ── Identify and exclude identifier columns (Transaction ID, User ID, UUIDs, hashes)
    const totalRows = overview.total_rows || previewRows.length;
    const identifierColumns = new Set(
      columnsInfo.filter(c => isIdentifierColumn(c, totalRows)).map(c => c.column_name)
    );

    // ── Numeric + categorical column lists (identifier + user-ignored columns excluded)
    const numCols = columnsInfo
      .filter(c => c.data_type === 'numeric' && !identifierColumns.has(c.column_name) && !ignoredColumns.has(c.column_name))
      .map(c => c.column_name);
    const catCols = columnsInfo
      .filter(c => c.data_type === 'categorical' && !identifierColumns.has(c.column_name) && !ignoredColumns.has(c.column_name))
      .map(c => c.column_name);

    // ── Bias categorical columns toward lower-cardinality (better for grouping)
    catCols.sort((a, b) => {
      const ca = columnsInfo.find(c => c.column_name === a)?.unique_count || 0;
      const cb = columnsInfo.find(c => c.column_name === b)?.unique_count || 0;
      // Prefer 2–20 unique values (ideal for pie/bar). Penalize very low (binary) and very high.
      const score = (n: number) => n >= 2 && n <= 20 ? n : n > 20 ? 100 + n : 50;
      return score(ca) - score(cb);
    });

    // ── 1. Pairwise correlations among numeric columns
    type CorrPair = { x: string; y: string; r: number; n: number; p: number };
    const correlations: CorrPair[] = [];
    for (let i = 0; i < numCols.length; i++) {
      for (let j = i + 1; j < numCols.length; j++) {
        const cx = numCols[i], cy = numCols[j];
        const xs: number[] = [], ys: number[] = [];
        for (const row of previewRows) {
          const vx = Number(row[cx]), vy = Number(row[cy]);
          if (!isNaN(vx) && !isNaN(vy) && row[cx] != null && row[cy] != null) {
            xs.push(vx); ys.push(vy);
          }
        }
        if (xs.length < 5) continue;
        const r = pearsonR(xs, ys);
        const p = correlationPValue(r, xs.length);
        if (isFinite(r) && Math.abs(r) > 0.1) {
          correlations.push({ x: cx, y: cy, r, n: xs.length, p });
        }
      }
    }
    correlations.sort((a, b) => Math.abs(b.r) - Math.abs(a.r));
    const topCorrelations = correlations.slice(0, 3);

    // ── 2. Outlier detection per numeric column (1.5 IQR rule)
    type OutlierInfo = { column: string; count: number; percentage: number; low: number; high: number };
    const outliers: OutlierInfo[] = [];
    for (const col of numCols) {
      const values = previewRows.map(r => Number(r[col])).filter(v => !isNaN(v));
      if (values.length < 10) continue;
      const sorted = [...values].sort((a, b) => a - b);
      const q1 = percentile(sorted, 0.25);
      const q3 = percentile(sorted, 0.75);
      const iqr = q3 - q1;
      if (iqr === 0) continue;
      const low = q1 - 1.5 * iqr;
      const high = q3 + 1.5 * iqr;
      const count = values.filter(v => v < low || v > high).length;
      const pct = (count / values.length) * 100;
      if (pct >= 2) {
        outliers.push({ column: col, count, percentage: pct, low, high });
      }
    }
    outliers.sort((a, b) => b.percentage - a.percentage);

    // ── 3. Auto-generated common questions
    const questions: string[] = [];
    if (catCols.length > 0 && numCols.length > 0) {
      questions.push(`What is the total ${humanize(numCols[0])} by ${humanize(catCols[0])}?`);
      questions.push(`Which ${humanize(catCols[0])} has the highest ${humanize(numCols[0])}?`);
    }
    if (numCols.length > 0) {
      questions.push(`What is the average ${humanize(numCols[0])}?`);
      questions.push(`Show me the top 10 rows by ${humanize(numCols[0])}`);
    }
    if (catCols.length > 0) {
      questions.push(`What are the most common values in ${humanize(catCols[0])}?`);
    }
    if (catCols.length >= 2) {
      questions.push(`Compare ${humanize(catCols[0])} and ${humanize(catCols[1])}`);
    }

    // ── 4. Dashboard widget recipes — best-practice chart curation
    const recipes: WidgetRecipe[] = [];

    // A) Time series → always a LINE chart (best practice)
    const timeCol = columnsInfo.find(c => /date|time|month|year|day|period|week|timestamp|created|updated/i.test(c.column_name));
    if (timeCol && numCols.length > 0) {
      recipes.push({
        id: 'timeseries',
        title: `${humanize(numCols[0])} over time`,
        subtitle: `Trend across ${humanize(timeCol.column_name)}`,
        insightTemplate: `Trend of ${humanize(numCols[0])} across ${humanize(timeCol.column_name)}.`,
        xColumn: timeCol.column_name, yColumn: numCols[0], agg: 'sum', chartType: 'line',
      });
    }

    // B) Low-cardinality categorical (2–6) + count → DONUT chart (best practice for share-of-whole)
    const lowCardCat = columnsInfo.find(c =>
      c.data_type === 'categorical' &&
      !identifierColumns.has(c.column_name) &&
      c.unique_count != null && c.unique_count >= 2 && c.unique_count <= 6
    );
    if (lowCardCat) {
      recipes.push({
        id: 'donut',
        title: `Share of ${humanize(lowCardCat.column_name)}`,
        subtitle: `How the data splits across ${lowCardCat.unique_count} groups`,
        insightTemplate: `Breakdown by ${humanize(lowCardCat.column_name)}.`,
        xColumn: lowCardCat.column_name, yColumn: lowCardCat.column_name, agg: 'count', chartType: 'pie',
      });
    }

    // C) Categorical (7–20) + numeric → BAR chart (best practice for ranked breakdowns)
    const midCardCat = columnsInfo.find(c =>
      c.data_type === 'categorical' &&
      !identifierColumns.has(c.column_name) &&
      c.unique_count != null && c.unique_count >= 3 && c.unique_count <= 20 &&
      (!lowCardCat || c.column_name !== lowCardCat.column_name)
    );
    const barCat = midCardCat || (catCols.length > 0 ? columnsInfo.find(c => c.column_name === catCols[0]) : null);
    if (barCat && numCols.length > 0) {
      recipes.push({
        id: 'topbar',
        title: `${humanize(numCols[0])} by ${humanize(barCat.column_name)}`,
        subtitle: `Total ${humanize(numCols[0])} per group`,
        insightTemplate: `Ranked breakdown of ${humanize(numCols[0])} across ${humanize(barCat.column_name)}.`,
        xColumn: barCat.column_name, yColumn: numCols[0], agg: 'sum', chartType: 'bar',
      });
    }

    // D) Strongest correlation → SCATTER chart (best practice for relationship viz)
    if (topCorrelations[0]) {
      const c = topCorrelations[0];
      recipes.push({
        id: 'corr1',
        title: `${humanize(c.x)} vs ${humanize(c.y)}`,
        subtitle: `${correlationStrength(c.r)} ${c.r > 0 ? 'positive' : 'negative'} correlation`,
        insightTemplate: `Relationship between ${humanize(c.x)} and ${humanize(c.y)}.`,
        xColumn: c.x, yColumn: c.y, agg: 'mean', chartType: 'scatter',
        correlation: { r: c.r, p: c.p, n: c.n },
      });
    }

    // E) Second correlation or second categorical breakdown
    if (topCorrelations[1] && recipes.length < 6) {
      const c = topCorrelations[1];
      recipes.push({
        id: 'corr2',
        title: `${humanize(c.x)} vs ${humanize(c.y)}`,
        subtitle: `${correlationStrength(c.r)} ${c.r > 0 ? 'positive' : 'negative'} correlation`,
        insightTemplate: `Second-strongest relationship in the data.`,
        xColumn: c.x, yColumn: c.y, agg: 'mean', chartType: 'scatter',
        correlation: { r: c.r, p: c.p, n: c.n },
      });
    }

    // F) Average of primary numeric by secondary categorical (if not already used)
    if (recipes.length < 6 && numCols.length > 0 && catCols.length > 1) {
      const secondCat = catCols.find(name => name !== (barCat?.column_name) && name !== (lowCardCat?.column_name));
      if (secondCat) {
        recipes.push({
          id: 'avgbar',
          title: `Average ${humanize(numCols[0])} by ${humanize(secondCat)}`,
          subtitle: `How each group compares on average`,
          insightTemplate: `Average ${humanize(numCols[0])} per ${humanize(secondCat)}.`,
          xColumn: secondCat, yColumn: numCols[0], agg: 'mean', chartType: 'bar',
        });
      }
    }

    // ── 5. Next steps
    const nextSteps: string[] = [];
    if (insights && insights.completeness < 95) {
      nextSteps.push(`Clean up missing values — ${formatInteger(insights.missingCount)} cells are empty. Use Filter to exclude rows with gaps.`);
    }
    if (outliers.length > 0) {
      nextSteps.push(`Review outliers in ${humanize(outliers[0].column)} — ${outliers[0].count} values (${outliers[0].percentage.toFixed(1)}%) look unusual.`);
    }
    if (numCols.length >= 2) {
      nextSteps.push(`Create a calculated column — e.g. a ratio or a category label based on thresholds.`);
    }
    if (topCorrelations.length > 0 && topCorrelations[0].p < 0.05) {
      nextSteps.push(`Dig into the relationship between ${humanize(topCorrelations[0].x)} and ${humanize(topCorrelations[0].y)} — it's statistically significant.`);
    }
    nextSteps.push(`Ask the AI a question in plain English using the Ask AI tab.`);

    return { topCorrelations, outliers: outliers.slice(0, 3), questions: questions.slice(0, 6), recipes: recipes.slice(0, 6), nextSteps };
  }, [previewRows, columnsInfo, insights, overview, ignoredColumns]);

  // ── Apply a chart recipe and jump to Chart tab
  const applyRecipe = (x: string, y: string, agg: 'sum' | 'mean' | 'median' | 'count', type: 'bar' | 'line' | 'pie' | 'scatter' | 'area') => {
    setChartX(x);
    setChartY(y);
    setChartAgg(agg);
    setChartType(type);
    setChartHidden(new Set());
    setTab('chart');
  };

  // ── Send a suggested question to AI and jump to AI tab
  const askAiQuestion = (q: string) => {
    setAiInput(q);
    setTab('ai');
  };

  // ── Build filter object for current UI state ────────────────────
  const buildFilterFromUI = (): Filter | null => {
    if (!filterCol || !currentFilterCol) return null;
    if (filterOp === 'equals') {
      if (!filterVal1) return null;
      return { column_name: filterCol, filter_type: 'equals', values: [filterVal1] };
    }
    if (filterOp === 'contains') {
      if (!filterVal1) return null;
      return { column_name: filterCol, filter_type: 'contains', values: [filterVal1] };
    }
    if (filterOp === 'greater_than') {
      const n = parseFloat(filterVal1);
      if (isNaN(n)) return null;
      return { column_name: filterCol, filter_type: 'range', values: { min: n } };
    }
    if (filterOp === 'less_than') {
      const n = parseFloat(filterVal1);
      if (isNaN(n)) return null;
      return { column_name: filterCol, filter_type: 'range', values: { max: n } };
    }
    if (filterOp === 'between') {
      const a = parseFloat(filterVal1), b = parseFloat(filterVal2);
      if (isNaN(a) || isNaN(b)) return null;
      return { column_name: filterCol, filter_type: 'range', values: { min: Math.min(a, b), max: Math.max(a, b) } };
    }
    return null;
  };

  const addFilter = () => {
    const f = buildFilterFromUI();
    if (!f) return;
    setActiveFilters(prev => [...prev, f]);
    setFilterVal1('');
    setFilterVal2('');
  };

  const removeFilter = (idx: number) => {
    setActiveFilters(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Preview active filters ──────────────────────────────────────
  useEffect(() => {
    if (tab !== 'filter') return;
    if (activeFilters.length === 0) {
      setFilterPreview(null);
      return;
    }
    setFilterBusy(true);
    fetch(`/api/filter/${dataset.table_name}/preview?limit=100`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ filters: activeFilters }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.success) {
          setFilterPreview({ rows: data.preview_data || [], count: data.total_rows || 0 });
        } else {
          setFilterPreview(null);
        }
      })
      .catch(() => setFilterPreview(null))
      .finally(() => setFilterBusy(false));
  }, [activeFilters, tab, dataset.table_name]);

  // ── Save filtered table permanently ────────────────────────────
  const saveFilteredTable = async () => {
    if (!saveFilterName.trim() || activeFilters.length === 0) return;
    setSavingFilter(true);
    try {
      const res = await fetch(`/api/filter/${dataset.table_name}/apply-permanent?new_table_name=${encodeURIComponent(saveFilterName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filters: activeFilters }),
      });
      if (res.ok) {
        setSaveFilterOpen(false);
        setSaveFilterName('');
        setActiveFilters([]);
        setFilterPreview(null);
      }
    } finally {
      setSavingFilter(false);
    }
  };

  // ── View SQL for current filters ────────────────────────────────
  const fetchViewSql = async () => {
    if (activeFilters.length === 0) return;
    setViewSqlBusy(true);
    try {
      const res = await fetch(`/api/filter/${dataset.table_name}/sql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filters: activeFilters }),
      });
      if (res.ok) {
        const data = await res.json();
        setViewSqlText(data.sql || data.query || JSON.stringify(data, null, 2));
      } else {
        setViewSqlText('-- Could not generate SQL');
      }
    } catch {
      setViewSqlText('-- Error generating SQL');
    } finally {
      setViewSqlBusy(false);
      setViewSqlOpen(true);
    }
  };

  // ── Single column deep analysis ───────────────────────────────
  const fetchColumnAnalysis = async (colName: string) => {
    setColumnAnalysisCol(colName);
    setColumnAnalysisBusy(true);
    setColumnAnalysisOpen(true);
    setColumnAnalysis(null);
    try {
      // Try enhanced endpoint first, fall back to basic
      let res = await fetch(`/api/enhanced/data/${dataset.table_name}/${colName}/analysis`, { credentials: 'include' });
      if (!res.ok) {
        res = await fetch(`/api/data/${dataset.table_name}/${colName}/analysis`, { credentials: 'include' });
      }
      if (res.ok) {
        const data = await res.json();
        setColumnAnalysis(data);
      }
    } catch {
      // silently fail
    } finally {
      setColumnAnalysisBusy(false);
    }
  };

  // ── Wrangle handlers ─────────────────────────────────────────────
  const runJoin = async () => {
    if (!joinTableB || !joinColA || !joinColB || !joinNewName) return;
    setWrangleBusy(true); setWrangleResult(null);
    try {
      const res = await fetch('/api/wrangle/join', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_a: dataset.table_name, table_b: joinTableB,
          join_col_a: joinColA, join_col_b: joinColB,
          join_type: joinType, new_table_name: joinNewName,
          project_id: project.id,
        }),
      });
      const d = await res.json();
      setWrangleResult(d.success
        ? { success: true, message: `✓ Joined ${d.row_count.toLocaleString()} rows saved as "${d.new_table_name}". Find it in your dataset list.` }
        : { success: false, message: d.error || 'Join failed.' });
    } catch { setWrangleResult({ success: false, message: 'Network error.' }); }
    finally { setWrangleBusy(false); }
  };

  const runDedup = async () => {
    if (!dedupNewName) return;
    setWrangleBusy(true); setWrangleResult(null);
    try {
      const res = await fetch(`/api/wrangle/${dataset.table_name}/deduplicate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: dedupCols, keep: dedupKeep, new_table_name: dedupNewName }),
      });
      const d = await res.json();
      setWrangleResult(d.success
        ? { success: true, message: `✓ ${d.row_count.toLocaleString()} unique rows saved as "${d.new_table_name}".` }
        : { success: false, message: d.error || 'Dedup failed.' });
    } catch { setWrangleResult({ success: false, message: 'Network error.' }); }
    finally { setWrangleBusy(false); }
  };

  const runFindDuplicates = async () => {
    if (dupCheckCols.length === 0) return;
    setWrangleBusy(true); setWrangleResult(null); setDupRows([]);
    try {
      const res = await fetch(
        `/api/wrangle/${dataset.table_name}/find-duplicates?columns=${encodeURIComponent(dupCheckCols.join(','))}&limit=200`,
        { credentials: 'include' }
      );
      const d = await res.json();
      if (d.success) { setDupRows(d.rows || []); setWrangleResult({ success: true, message: `Found ${d.count} duplicate rows.`, rows: d.rows }); }
      else setWrangleResult({ success: false, message: d.error || 'Failed.' });
    } catch { setWrangleResult({ success: false, message: 'Network error.' }); }
    finally { setWrangleBusy(false); }
  };

  const runSort = async () => {
    const validSorts = sortCols.filter(s => s.column);
    if (!validSorts.length || !sortNewName) return;
    setWrangleBusy(true); setWrangleResult(null);
    try {
      const res = await fetch(`/api/wrangle/${dataset.table_name}/sort`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sort_by: validSorts, new_table_name: sortNewName }),
      });
      const d = await res.json();
      setWrangleResult(d.success
        ? { success: true, message: `✓ Sorted dataset (${d.row_count.toLocaleString()} rows) saved as "${d.new_table_name}".` }
        : { success: false, message: d.error || 'Sort failed.' });
    } catch { setWrangleResult({ success: false, message: 'Network error.' }); }
    finally { setWrangleBusy(false); }
  };

  const runUniqueValues = async () => {
    if (!uniqueCol) return;
    setWrangleBusy(true); setWrangleResult(null); setUniqueRows([]);
    try {
      const res = await fetch(
        `/api/wrangle/${dataset.table_name}/unique-values?column=${encodeURIComponent(uniqueCol)}&limit=500`,
        { credentials: 'include' }
      );
      const d = await res.json();
      if (d.success) { setUniqueRows(d.rows || []); setWrangleResult({ success: true, message: `${d.unique_count} distinct values in "${uniqueCol}".` }); }
      else setWrangleResult({ success: false, message: d.error || 'Failed.' });
    } catch { setWrangleResult({ success: false, message: 'Network error.' }); }
    finally { setWrangleBusy(false); }
  };

  // ── Add column: preview + apply ─────────────────────────────────
  const previewAddColumn = async () => {
    if (!addName.trim() || !addDesc.trim()) return;
    setAddBusy(true);
    setAddResult(null);
    try {
      const res = await fetch(`/api/augment/${dataset.table_name}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description: addDesc, new_column_name: addName }),
      });
      const data = await res.json();
      if (data?.success) {
        setAddPreview({ sql: data.sql || '', explanation: data.explanation || '' });
      } else {
        setAddResult({ success: false, message: data?.error || data?.message || 'Could not generate preview.' });
      }
    } catch {
      setAddResult({ success: false, message: 'Network error. Please try again.' });
    } finally {
      setAddBusy(false);
    }
  };

  const applyAddColumn = async () => {
    setAddBusy(true);
    try {
      const res = await fetch(`/api/augment/${dataset.table_name}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ description: addDesc, new_column_name: addName }),
      });
      const data = await res.json();
      setAddResult({ success: !!data?.success, message: data?.message || (data?.success ? 'Column added.' : 'Could not add column.') });
      if (data?.success) {
        // refresh columns info + preview
        const [colsRes, prevRes] = await Promise.all([
          fetch(`/api/filter/${dataset.table_name}/columns`, { credentials: 'include' }),
          fetch(`/api/tables/${dataset.table_name}/query?limit=500`, { credentials: 'include' }),
        ]);
        if (colsRes.ok) { const d = await colsRes.json(); setColumnsInfo(d.columns || []); }
        if (prevRes.ok) setPreviewRows(await prevRes.json());
        setAddPreview(null);
        setAddName('');
        setAddDesc('');
      }
    } catch {
      setAddResult({ success: false, message: 'Network error.' });
    } finally {
      setAddBusy(false);
    }
  };

  // ── Chart: build locally from preview rows (fast, animated) ─────
  const chartResult = useMemo(() => {
    if (!chartX || !chartY || previewRows.length === 0) return null;
    const groups: Record<string, number[]> = {};
    for (const row of previewRows) {
      const key = String(row[chartX] ?? '—');
      const val = Number(row[chartY]);
      if (!groups[key]) groups[key] = [];
      if (!isNaN(val)) groups[key].push(val);
    }
    const agg = (vals: number[]) => {
      if (vals.length === 0) return 0;
      if (chartAgg === 'sum') return vals.reduce((a, b) => a + b, 0);
      if (chartAgg === 'mean') return vals.reduce((a, b) => a + b, 0) / vals.length;
      if (chartAgg === 'count') return vals.length;
      if (chartAgg === 'median') { const s = [...vals].sort((a,b)=>a-b); return s[Math.floor(s.length/2)]; }
      return 0;
    };
    let entries = Object.entries(groups).map(([k, v]) => [k, agg(v)] as [string, number]);
    entries.sort((a, b) => b[1] - a[1]);
    // cap to top 20 for readability
    if (entries.length > 20) entries = entries.slice(0, 20);
    return { labels: entries.map(e => e[0]), values: entries.map(e => e[1]) };
  }, [previewRows, chartX, chartY, chartAgg]);

  // hidden categories → filter chart display
  const visibleChart = useMemo(() => {
    if (!chartResult) return null;
    const labels: string[] = [];
    const values: number[] = [];
    for (let i = 0; i < chartResult.labels.length; i++) {
      if (!chartHidden.has(chartResult.labels[i])) {
        labels.push(chartResult.labels[i]);
        values.push(chartResult.values[i]);
      }
    }
    return { labels, values };
  }, [chartResult, chartHidden]);

  const toggleChartCat = (label: string) => {
    setChartHidden(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  // Y axis format detection
  const yType = useMemo(() => detectColumnType(chartY, previewRows.slice(0, 20).map(r => r[chartY])), [chartY, previewRows]);

  // ── AI: send question ──────────────────────────────────────────
  const sendAi = async () => {
    if (!aiInput.trim() || aiBusy) return;
    const q = aiInput.trim();
    setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', text: q }, { role: 'ai', text: '', loading: true }]);
    setAiBusy(true);
    try {
      const res = await fetch(`/api/analysis/${dataset.table_name}/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: q }),
      });
      const data = await res.json();
      setAiMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (data?.success) {
          last.loading = false;
          last.text = data.explanation || data.business_value || 'Here are the results.';
          last.data = data.data || (data.analyses?.[0]?.data) || [];
          last.sql = data.sql;
          last.insights = data.key_insights || data.analyses?.[0]?.key_insights || [];
        } else {
          last.loading = false;
          last.text = data?.error || data?.explanation || 'I could not answer that. Try rephrasing your question.';
        }
        return updated;
      });
    } catch {
      setAiMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        last.loading = false;
        last.text = 'Network error. Please try again.';
        return updated;
      });
    } finally {
      setAiBusy(false);
    }
  };

  // ── Suggested prompts ───────────────────────────────────────────
  const suggestedPrompts = [
    `What are the top 5 values in ${categoricalCols[0] || 'this dataset'}?`,
    numericCols[0] ? `What is the total ${numericCols[0]}?` : 'What is the biggest trend?',
    categoricalCols[0] && numericCols[0] ? `Show me ${numericCols[0]} by ${categoricalCols[0]}` : 'Summarize my data',
    'What stands out most in this data?',
  ];

  // ── Render ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <CircularProgress size={28} sx={{ color: '#111' }} />
        <Typography sx={{ mt: 2, color: '#6B7280', fontSize: '0.875rem' }}>Loading your dataset…</Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Go back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#F7F8FA', overflow: 'hidden', minWidth: 0 }}>
      {/* Context header */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E5E7EB', px: { xs: 2, md: 3 }, py: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
          <Button startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />} onClick={onBack} size="small"
            sx={{ color: '#6B7280', textTransform: 'none', fontSize: '0.8rem', p: 0, minWidth: 'auto' }}>
            Back to datasets
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: { xs: '1.1rem', md: '1.375rem' }, fontWeight: 700, color: '#111', letterSpacing: '-0.02em' }}>
            {dataset.name}
          </Typography>
          <Chip size="small" label={`${formatInteger(overview?.total_rows || 0)} rows`}
            sx={{ bgcolor: '#F3F4F6', color: '#374151', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
          <Chip size="small" label={`${overview?.total_columns || 0} columns`}
            sx={{ bgcolor: '#F3F4F6', color: '#374151', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
              onClick={() => {
                const link = document.createElement('a');
                link.href = `/api/tables/${dataset.table_name}/export?format=csv`;
                link.download = `${dataset.name}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, color: '#374151', border: '1px solid #E5E7EB', px: 1.5, py: 0.5, borderRadius: '8px', '&:hover': { bgcolor: '#F9FAFB' } }}>
              Export CSV
            </Button>
            <Typography sx={{ fontSize: '0.8rem', color: '#9CA3AF' }}>
              {project.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs — horizontally scrollable on mobile */}
      <Box sx={{
        bgcolor: '#fff', borderBottom: '1px solid #E5E7EB', flexShrink: 0, minWidth: 0,
        overflowX: 'auto', overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none' as any, scrollbarWidth: 'none' as any,
      }}>
        <Box sx={{ display: 'inline-flex', gap: 0, minWidth: 'max-content', px: { xs: 1, md: 3 } }}>
          {TABS.map(t => (
            <Button key={t.id}
              startIcon={t.icon}
              onClick={() => setTab(t.id)}
              sx={{
                textTransform: 'none', fontWeight: tab === t.id ? 700 : 500, fontSize: '0.82rem',
                px: 1.5, py: 1.25, borderRadius: 0, mb: '-1px', minWidth: 'auto', whiteSpace: 'nowrap',
                color: tab === t.id ? '#111' : '#6B7280',
                bgcolor: 'transparent',
                borderBottom: tab === t.id ? '2px solid #111' : '2px solid transparent',
                '&:hover': { bgcolor: '#F9FAFB', color: '#111' },
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}>
              {t.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Tab content — scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>

        {/* ═════════════ OVERVIEW TAB ═════════════ */}
        {tab === 'explore' && overview && insights && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* KPI row */}
            <SectionHeader title="At a glance" subtitle="A quick snapshot of your dataset" />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
              <KpiCard label="Total Rows" value={formatInteger(insights.rows)} icon={<StorageIcon sx={{ fontSize: 18 }} />} color="#3B82F6" hint="Records in this dataset" />
              <KpiCard label="Columns" value={String(insights.cols)} icon={<BarChartIcon sx={{ fontSize: 18 }} />} color="#10B981" hint={`${insights.numeric} numeric, ${insights.categorical} text`} />
              <KpiCard label="Data Quality" value={`${insights.completeness.toFixed(1)}%`} icon={<CheckCircleIcon sx={{ fontSize: 18 }} />} color={insights.completeness > 95 ? '#10B981' : insights.completeness > 80 ? '#F59E0B' : '#EF4444'} hint={insights.missingCount > 0 ? `${formatInteger(insights.missingCount)} missing values` : 'No missing values'} />
              <KpiCard label="Number Fields" value={String(insights.numeric)} icon={<TrendingUpIcon sx={{ fontSize: 18 }} />} color="#8B5CF6" hint="Can be used in math" />
              <KpiCard label="Text Fields" value={String(insights.categorical)} icon={<InsightsIcon sx={{ fontSize: 18 }} />} color="#F59E0B" hint="Categories & labels" />
            </Box>

            {/* ─── Smart Insights panel ─── */}
            {smartInsights && (
              <Box sx={{ mb: 4 }}>
                <SectionHeader
                  title="Smart Insights"
                  subtitle="Automatic analysis tailored to your data"
                  right={<Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 13 }} />} label="Auto-generated" sx={{ bgcolor: '#F5F3FF', color: '#7C3AED', fontWeight: 600, fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { color: '#7C3AED', ml: 0.5 } }} />}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2 }}>

                  {/* Strongest Relationships */}
                  {smartInsights.topCorrelations.length > 0 && (
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <CompareArrowsIcon sx={{ fontSize: 18, color: '#7C3AED' }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111' }}>Strongest Relationships</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip size="small" icon={<BarChartIcon sx={{ fontSize: 12 }} />} label="Opens Chart"
                          sx={{ bgcolor: '#F5F3FF', color: '#7C3AED', fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: '#7C3AED', ml: 0.5 } }} />
                      </Box>
                      {smartInsights.topCorrelations.map((c, i) => {
                        const significant = c.p < 0.05;
                        const direction = c.r > 0 ? 'positive' : 'negative';
                        const color = c.r > 0 ? '#10B981' : '#EF4444';
                        return (
                          <Box key={i} onClick={() => applyRecipe(c.x, c.y, 'mean', 'scatter')}
                            sx={{ py: 1, px: 1.25, borderRadius: '8px', cursor: 'pointer', mb: 0.5, '&:hover': { bgcolor: '#F9FAFB' }, border: '1px solid transparent', '&:hover .arrow': { opacity: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111', lineHeight: 1.3 }}>
                                  {humanize(c.x)} ↔ {humanize(c.y)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4, flexWrap: 'wrap' }}>
                                  <Chip label={`${correlationStrength(c.r)} ${direction}`} size="small" sx={{ bgcolor: color + '18', color, fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                                  <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>r = <b style={{ color: '#111' }}>{c.r.toFixed(2)}</b></Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>
                                    p = <b style={{ color: significant ? '#059669' : '#6B7280' }}>{formatPValue(c.p)}</b>
                                    {significant && <span style={{ color: '#059669', marginLeft: 4 }}>✓ significant</span>}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>n = {c.n}</Typography>
                                </Box>
                              </Box>
                              <ArrowForwardIcon className="arrow" sx={{ fontSize: 16, color: '#7C3AED', opacity: 0.6, transition: 'opacity 0.15s' }} />
                            </Box>
                          </Box>
                        );
                      })}
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', mt: 1, fontStyle: 'italic' }}>
                        Click any pair to visualize it. <b>p &lt; 0.05</b> means the relationship is statistically significant.
                      </Typography>
                    </Box>
                  )}

                  {/* Outliers / Anomalies */}
                  {smartInsights.outliers.length > 0 && (
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <WarningAmberIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111' }}>Anomalies Detected</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip size="small" icon={<FilterAltIcon sx={{ fontSize: 12 }} />} label="Opens Filter"
                          sx={{ bgcolor: '#FFFBEB', color: '#B45309', fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: '#B45309', ml: 0.5 } }} />
                      </Box>
                      {smartInsights.outliers.map((o, i) => (
                        <Box key={i} onClick={() => {
                            setFilterCol(o.column);
                            setFilterOp('between');
                            setFilterVal1(String(Math.round(o.low * 100) / 100));
                            setFilterVal2(String(Math.round(o.high * 100) / 100));
                            setTab('filter');
                          }}
                          sx={{ py: 1, px: 1.25, borderRadius: '8px', mb: 0.5, bgcolor: i === 0 ? '#FFFBEB' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: '#FEF3C7' } }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111', mb: 0.25 }}>
                              {humanize(o.column)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.74rem', color: '#6B7280', lineHeight: 1.45 }}>
                              <b>{o.count} values</b> ({o.percentage.toFixed(1)}%) fall outside the typical range of <b>{autoFormat(o.low, o.column)}</b> to <b>{autoFormat(o.high, o.column)}</b>.
                            </Typography>
                          </Box>
                          <ArrowForwardIcon sx={{ fontSize: 14, color: '#B45309', flexShrink: 0 }} />
                        </Box>
                      ))}
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', mt: 0.5, fontStyle: 'italic' }}>
                        Click to open Filter tab with these outliers pre-excluded.
                      </Typography>
                    </Box>
                  )}

                  {/* Recommended Charts */}
                  {smartInsights.recipes.length > 0 && (
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.25, gridColumn: { xs: 'auto', md: smartInsights.topCorrelations.length > 0 || smartInsights.outliers.length > 0 ? 'auto' : 'span 2' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <BarChartIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111' }}>Charts to Try</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip size="small" icon={<BarChartIcon sx={{ fontSize: 12 }} />} label="Opens Chart"
                          sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: '#1D4ED8', ml: 0.5 } }} />
                      </Box>
                      {smartInsights.recipes.map((rec, i) => {
                        const chartIcon = rec.chartType === 'pie' ? <PieChartOutlineIcon sx={{ fontSize: 17, color: '#3B82F6' }} />
                          : rec.chartType === 'line' ? <ShowChartIcon sx={{ fontSize: 17, color: '#3B82F6' }} />
                          : rec.chartType === 'scatter' ? <ScatterPlotIcon sx={{ fontSize: 17, color: '#3B82F6' }} />
                          : <BarChartIcon sx={{ fontSize: 17, color: '#3B82F6' }} />;
                        return (
                          <Box key={i} onClick={() => applyRecipe(rec.xColumn, rec.yColumn, rec.agg, rec.chartType)}
                            sx={{ py: 1.1, px: 1.25, borderRadius: '8px', cursor: 'pointer', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1.5, '&:hover': { bgcolor: '#F0F9FF' }, '&:hover .rec-arrow': { opacity: 1, transform: 'translateX(2px)' } }}>
                            <Box sx={{ width: 32, height: 32, borderRadius: '8px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {chartIcon}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>{rec.title}</Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', lineHeight: 1.4 }}>{rec.subtitle}</Typography>
                            </Box>
                            <ArrowForwardIcon className="rec-arrow" sx={{ fontSize: 16, color: '#3B82F6', opacity: 0, transition: 'all 0.15s' }} />
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Common Questions */}
                  {smartInsights.questions.length > 0 && (
                    <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <QuestionAnswerOutlinedIcon sx={{ fontSize: 18, color: '#10B981' }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#111' }}>Common Questions</Typography>
                        <Box sx={{ flex: 1 }} />
                        <Chip size="small" icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />} label="Opens Ask AI"
                          sx={{ bgcolor: '#F0FDF4', color: '#059669', fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: '#059669', ml: 0.5 } }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {smartInsights.questions.map((q, i) => (
                          <Box key={i} onClick={() => askAiQuestion(q)}
                            sx={{ py: 0.9, px: 1.25, borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: '#F0FDF4' }, '&:hover .q-arrow': { opacity: 1, transform: 'translateX(2px)' } }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', flex: 1, lineHeight: 1.45 }}>{q}</Typography>
                            <ArrowForwardIcon className="q-arrow" sx={{ fontSize: 14, color: '#10B981', opacity: 0, transition: 'all 0.15s' }} />
                          </Box>
                        ))}
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: '#9CA3AF', mt: 1, fontStyle: 'italic' }}>
                        Click any question to ask the AI.
                      </Typography>
                    </Box>
                  )}

                  {/* Next Steps */}
                  {smartInsights.nextSteps.length > 0 && (
                    <Box sx={{ bgcolor: 'linear-gradient(135deg, #F0F9FF 0%, #EFF6FF 100%)', background: 'linear-gradient(135deg, #F0F9FF 0%, #EFF6FF 100%)', border: '1px solid #BFDBFE', borderRadius: '12px', p: 2.25, gridColumn: { xs: 'auto', md: 'span 2' } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <LightbulbOutlinedIcon sx={{ fontSize: 18, color: '#1D4ED8' }} />
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#1E3A8A' }}>Recommended Next Steps</Typography>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1 }}>
                        {smartInsights.nextSteps.map((step, i) => (
                          <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#1D4ED8', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0, mt: 0.15 }}>
                              {i + 1}
                            </Box>
                            <Typography sx={{ fontSize: '0.8rem', color: '#1E3A8A', lineHeight: 1.5 }}>{step}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Column list */}
            <SectionHeader title="What's in this dataset?" subtitle={`Your columns with sample values${ignoredColumns.size > 0 ? ` · ${ignoredColumns.size} ignored` : ''}`} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 1fr))' }, gap: 1.5, mb: 4 }}>
              {columnsInfo.map(col => {
                const type = col.data_type === 'numeric' ? 'Number' : 'Text';
                const typeColor = col.data_type === 'numeric' ? '#8B5CF6' : '#3B82F6';
                const isIgnored = ignoredColumns.has(col.column_name);
                return (
                  <Box key={col.column_name}
                    onClick={() => !isIgnored && fetchColumnAnalysis(col.column_name)}
                    sx={{ border: '1px solid #E5E7EB', borderRadius: '10px', p: 1.75, bgcolor: '#fff', opacity: isIgnored ? 0.4 : 1, transition: 'all 0.15s', position: 'relative', cursor: isIgnored ? 'default' : 'pointer', '&:hover': isIgnored ? {} : { borderColor: '#A5B4FC', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' } }}>
                    {isIgnored && (
                      <Chip size="small" label="Ignored" icon={<VisibilityOffIcon sx={{ fontSize: 11 }} />}
                        sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: '0.6rem', height: 18, '& .MuiChip-icon': { color: '#DC2626', ml: 0.5 } }} />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.75 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: isIgnored ? '#9CA3AF' : '#111', wordBreak: 'break-word', flex: 1, textDecoration: isIgnored ? 'line-through' : 'none' }}>
                        {col.column_name}
                      </Typography>
                      {!isIgnored && <Chip label={type} size="small" sx={{ bgcolor: typeColor + '15', color: typeColor, fontWeight: 600, fontSize: '0.65rem', height: 18, ml: 1 }} />}
                    </Box>
                    {!isIgnored && col.data_type === 'numeric' && col.min_value != null && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>
                        Range: {autoFormat(col.min_value, col.column_name)} — {autoFormat(col.max_value, col.column_name)}
                      </Typography>
                    )}
                    {!isIgnored && col.data_type === 'categorical' && col.top_values && col.top_values.length > 0 && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>
                        {col.unique_count} unique · most common: <b>{col.top_values[0].value}</b>
                      </Typography>
                    )}
                    {!isIgnored && (
                      <Typography sx={{ fontSize: '0.65rem', color: '#A5B4FC', mt: 0.5, fontWeight: 500 }}>Click for deep analysis →</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Data preview */}
            <SectionHeader
              title="Your data"
              subtitle={`Showing ${Math.min(PAGE_SIZE, visiblePreview.length)} of ${formatInteger(visiblePreview.length)} loaded rows`}
              right={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button size="small" variant="outlined"
                    onClick={e => setColPickerAnchor(e.currentTarget)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, borderColor: '#E5E7EB', color: '#374151', borderRadius: '8px', px: 1.5, py: 0.5, '&:hover': { bgcolor: '#F9FAFB' } }}>
                    Columns ({previewVisibleCols ? previewVisibleCols.size : allColumns.length}/{allColumns.length})
                  </Button>
                  <Menu anchorEl={colPickerAnchor} open={Boolean(colPickerAnchor)} onClose={() => setColPickerAnchor(null)}
                    PaperProps={{ sx: { maxHeight: 400, width: 260 } }}>
                    <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => setPreviewVisibleCols(null)} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>All</Button>
                      <Button size="small" onClick={() => setPreviewVisibleCols(new Set(numericCols))} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Numeric only</Button>
                      <Button size="small" onClick={() => setPreviewVisibleCols(new Set(categoricalCols))} sx={{ textTransform: 'none', fontSize: '0.72rem' }}>Text only</Button>
                    </Box>
                    <Divider />
                    {allColumns.map(col => (
                      <MenuItem key={col} dense onClick={() => {
                        setPreviewVisibleCols(prev => {
                          const current = prev || new Set(allColumns);
                          const next = new Set(current);
                          if (next.has(col)) { if (next.size > 1) next.delete(col); }
                          else next.add(col);
                          return next;
                        });
                      }} sx={{ fontSize: '0.82rem' }}>
                        <Checkbox size="small" checked={previewVisibleCols ? previewVisibleCols.has(col) : true} sx={{ p: 0, mr: 1 }} />
                        {col}
                      </MenuItem>
                    ))}
                  </Menu>
                  <TextField size="small" placeholder="Search…" value={search}
                    onChange={e => { setSearch(e.target.value); setPage(0); }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /></InputAdornment> }}
                    sx={{ width: { xs: '100%', sm: 200 }, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '0.8rem', bgcolor: '#fff' } }}
                  />
                </Box>
              }
            />
            {(() => {
              const tableCols = previewVisibleCols ? allColumns.filter(c => previewVisibleCols.has(c)) : allColumns;
              return (
            <Box sx={{ border: '1px solid #E5E7EB', borderRadius: '10px', bgcolor: '#fff', overflow: 'hidden' }}>
              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tableCols.length}, minmax(140px, 1fr))`, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  {tableCols.map(col => (
                    <Box key={col} sx={{ px: 2, py: 1.25 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', whiteSpace: 'nowrap' }}>
                        {col.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {pagePreview.map((row, i) => (
                  <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${tableCols.length}, minmax(140px, 1fr))`, borderBottom: i === pagePreview.length - 1 ? 'none' : '1px solid #F3F4F6', '&:hover': { bgcolor: '#FAFBFC' } }}>
                    {tableCols.map(col => (
                      <Box key={col} sx={{ px: 2, py: 1.125 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {autoFormat(row[col], col, previewRows.slice(0, 20).map(r => r[col]))}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ))}
                {pagePreview.length === 0 && (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>No rows match your search.</Typography>
                  </Box>
                )}
              </Box>
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, borderTop: '1px solid #E5E7EB', bgcolor: '#FAFBFC' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>
                    Page {page + 1} of {totalPages}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Previous</Button>
                    <Button size="small" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Next</Button>
                  </Box>
                </Box>
              )}
            </Box>
              );
            })()}

            {/* Column Analysis Dialog */}
            <Dialog open={columnAnalysisOpen} onClose={() => setColumnAnalysisOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {humanize(columnAnalysisCol)} — Deep Analysis
                <IconButton size="small" onClick={() => setColumnAnalysisOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
              </DialogTitle>
              <DialogContent>
                {columnAnalysisBusy ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
                ) : columnAnalysis ? (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    {/* Basic stats */}
                    {columnAnalysis.statistics && (
                      <Box sx={{ bgcolor: '#F9FAFB', borderRadius: '10px', p: 2, border: '1px solid #E5E7EB' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>Statistics</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                          {Object.entries(columnAnalysis.statistics).map(([key, val]) => (
                            <Box key={key}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase' }}>{key.replace(/_/g, ' ')}</Typography>
                              <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#111' }}>{typeof val === 'number' ? autoFormat(val, columnAnalysisCol) : String(val ?? '—')}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                    {/* Distribution / top values */}
                    {columnAnalysis.distribution && (
                      <Box sx={{ bgcolor: '#F9FAFB', borderRadius: '10px', p: 2, border: '1px solid #E5E7EB' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>Distribution</Typography>
                        {Array.isArray(columnAnalysis.distribution) ? columnAnalysis.distribution.slice(0, 10).map((item: any, i: number) => (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: i < Math.min(columnAnalysis.distribution.length, 10) - 1 ? '1px solid #E5E7EB' : 'none' }}>
                            <Typography sx={{ fontSize: '0.82rem', color: '#111' }}>{String(item.value ?? item.bin ?? item.label ?? '—')}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>{typeof item.count === 'number' ? formatInteger(item.count) : String(item.frequency ?? item.count ?? '')}</Typography>
                          </Box>
                        )) : (
                          <Typography sx={{ fontSize: '0.82rem', color: '#6B7280' }}>{JSON.stringify(columnAnalysis.distribution)}</Typography>
                        )}
                      </Box>
                    )}
                    {/* Outliers */}
                    {columnAnalysis.outliers && (
                      <Box sx={{ bgcolor: '#FFFBEB', borderRadius: '10px', p: 2, border: '1px solid #FDE68A' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Outliers</Typography>
                        <Typography sx={{ fontSize: '0.82rem', color: '#92400E' }}>
                          {columnAnalysis.outliers.count != null ? `${columnAnalysis.outliers.count} outlier${columnAnalysis.outliers.count !== 1 ? 's' : ''} detected` : JSON.stringify(columnAnalysis.outliers)}
                        </Typography>
                      </Box>
                    )}
                    {/* Raw JSON fallback for any extra fields */}
                    {!columnAnalysis.statistics && !columnAnalysis.distribution && (
                      <Box sx={{ bgcolor: '#F9FAFB', borderRadius: '10px', p: 2, border: '1px solid #E5E7EB' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Analysis</Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#374151', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {JSON.stringify(columnAnalysis, null, 2)}
                        </Box>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ color: '#6B7280', py: 2 }}>No analysis available for this column.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setColumnAnalysisOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* ═════════════ FILTER TAB ═════════════ */}
        {tab === 'filter' && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <SectionHeader title="Narrow down your data" subtitle="Keep only the rows that match your conditions" />

            {/* Filter builder */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 1.5, md: 2.5 }, mb: 3 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1.5 }}>
                Show me rows where:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Column */}
                <Select size="small" value={filterCol} onChange={e => { setFilterCol(e.target.value); setFilterVal1(''); setFilterVal2(''); }}
                  sx={{ minWidth: { xs: '100%', sm: 180 }, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                  {columnsInfo.map(c => (
                    <MenuItem key={c.column_name} value={c.column_name} sx={{ fontSize: '0.85rem' }}>
                      {c.column_name} <span style={{ color: '#9CA3AF', marginLeft: 6, fontSize: '0.7rem' }}>({c.data_type === 'numeric' ? 'number' : 'text'})</span>
                    </MenuItem>
                  ))}
                </Select>

                {/* Operator */}
                <Select size="small" value={filterOp} onChange={e => setFilterOp(e.target.value as any)}
                  sx={{ minWidth: 150, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                  {currentFilterCol?.data_type === 'numeric' ? [
                    <MenuItem key="equals" value="equals" sx={{ fontSize: '0.85rem' }}>equals</MenuItem>,
                    <MenuItem key="greater_than" value="greater_than" sx={{ fontSize: '0.85rem' }}>is greater than</MenuItem>,
                    <MenuItem key="less_than" value="less_than" sx={{ fontSize: '0.85rem' }}>is less than</MenuItem>,
                    <MenuItem key="between" value="between" sx={{ fontSize: '0.85rem' }}>is between</MenuItem>,
                  ] : [
                    <MenuItem key="equals" value="equals" sx={{ fontSize: '0.85rem' }}>is</MenuItem>,
                    <MenuItem key="contains" value="contains" sx={{ fontSize: '0.85rem' }}>contains</MenuItem>,
                  ]}
                </Select>

                {/* Value(s) */}
                {currentFilterCol?.data_type === 'categorical' && filterOp === 'equals' && currentFilterCol.top_values && currentFilterCol.top_values.length > 0 ? (
                  <Select size="small" value={filterVal1} onChange={e => setFilterVal1(e.target.value)} displayEmpty
                    sx={{ minWidth: 180, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                    <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Pick a value…</MenuItem>
                    {currentFilterCol.top_values.map(tv => (
                      <MenuItem key={tv.value} value={tv.value} sx={{ fontSize: '0.85rem' }}>
                        {tv.value} <span style={{ color: '#9CA3AF', marginLeft: 6, fontSize: '0.7rem' }}>({tv.frequency})</span>
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <TextField size="small" placeholder="value" value={filterVal1} onChange={e => setFilterVal1(e.target.value)}
                    type={currentFilterCol?.data_type === 'numeric' ? 'number' : 'text'}
                    sx={{ minWidth: 140, bgcolor: '#F9FAFB', '& .MuiOutlinedInput-input': { py: 1, fontSize: '0.85rem' } }} />
                )}

                {filterOp === 'between' && (
                  <>
                    <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>and</Typography>
                    <TextField size="small" placeholder="max" value={filterVal2} onChange={e => setFilterVal2(e.target.value)} type="number"
                      sx={{ minWidth: 140, bgcolor: '#F9FAFB', '& .MuiOutlinedInput-input': { py: 1, fontSize: '0.85rem' } }} />
                  </>
                )}

                <Button variant="contained" onClick={addFilter} disabled={!filterVal1}
                  sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 0.9, '&:hover': { bgcolor: '#2D2D2D' } }}>
                  Add condition
                </Button>
              </Box>

              {/* Hints for numeric columns */}
              {currentFilterCol?.data_type === 'numeric' && (
                <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mt: 1.25 }}>
                  Values in this column range from <b>{autoFormat(currentFilterCol.min_value, currentFilterCol.column_name)}</b> to <b>{autoFormat(currentFilterCol.max_value, currentFilterCol.column_name)}</b>
                </Typography>
              )}
            </Box>

            {/* Active filters */}
            {activeFilters.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>Active conditions ({activeFilters.length}):</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {activeFilters.map((f, i) => {
                    const desc = f.filter_type === 'range'
                      ? (f.values.min != null && f.values.max != null ? `${f.column_name} between ${f.values.min} and ${f.values.max}`
                        : f.values.min != null ? `${f.column_name} > ${f.values.min}` : `${f.column_name} < ${f.values.max}`)
                      : `${f.column_name} ${f.filter_type === 'contains' ? 'contains' : '='} ${(f.values as any[])[0]}`;
                    return (
                      <Chip key={i} label={desc} onDelete={() => removeFilter(i)}
                        sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 500, fontSize: '0.75rem', '& .MuiChip-deleteIcon': { color: '#1D4ED8' } }} />
                    );
                  })}
                  <Button size="small" onClick={() => setActiveFilters([])} sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#6B7280' }}>Clear all</Button>
                </Box>
              </Box>
            )}

            {/* Preview */}
            {activeFilters.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed #D1D5DB', borderRadius: '12px', bgcolor: '#fff' }}>
                <FilterAltIcon sx={{ fontSize: 32, color: '#D1D5DB', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>Add conditions above to filter your data.</Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>
                    {filterBusy ? 'Calculating…' : filterPreview ? `${formatInteger(filterPreview.count)} rows match (preview: first 100)` : 'No preview'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {activeFilters.length > 0 && (
                      <Button size="small" variant="outlined" onClick={fetchViewSql} disabled={viewSqlBusy}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, borderColor: '#E5E7EB', color: '#374151', '&:hover': { bgcolor: '#F9FAFB' } }}>
                        {viewSqlBusy ? 'Loading…' : 'View SQL'}
                      </Button>
                    )}
                    {filterPreview && filterPreview.count > 0 && (
                      <Button size="small" variant="contained" onClick={() => setSaveFilterOpen(true)}
                        sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, '&:hover': { bgcolor: '#2D2D2D' } }}>
                        Save as new dataset
                      </Button>
                    )}
                  </Box>
                </Box>
                {filterBusy && <LinearProgress sx={{ height: 2 }} />}
                {filterPreview && filterPreview.rows.length > 0 && (
                  <Box sx={{ overflowX: 'auto', maxHeight: 500 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${allColumns.length}, minmax(140px, 1fr))`, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0 }}>
                      {allColumns.map(col => (
                        <Box key={col} sx={{ px: 2, py: 1.25 }}>
                          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', whiteSpace: 'nowrap' }}>{col.replace(/_/g, ' ')}</Typography>
                        </Box>
                      ))}
                    </Box>
                    {filterPreview.rows.slice(0, 50).map((row, i) => (
                      <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${allColumns.length}, minmax(140px, 1fr))`, borderBottom: '1px solid #F3F4F6' }}>
                        {allColumns.map(col => (
                          <Box key={col} sx={{ px: 2, py: 1.125 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {autoFormat(row[col], col)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Save dialog */}
            <Dialog open={saveFilterOpen} onClose={() => setSaveFilterOpen(false)}>
              <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>Save filtered dataset</DialogTitle>
              <DialogContent>
                <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', mb: 1 }}>
                  This creates a new dataset with only the {filterPreview ? formatInteger(filterPreview.count) : ''} matching rows. Give it a descriptive name:
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', mb: 2 }}>
                  Tip: use a name like "high_value_customers" or "q1_sales_north" so you can find it easily later.
                </Typography>
                <TextField autoFocus fullWidth size="small"
                  placeholder={(() => {
                    // Auto-suggest a name from filter conditions
                    if (activeFilters.length > 0) {
                      const f = activeFilters[0];
                      const val = f.filter_type === 'range'
                        ? (f.values.min != null && f.values.max != null ? `${f.values.min}_to_${f.values.max}` : f.values.min != null ? `gt_${f.values.min}` : `lt_${f.values.max}`)
                        : String((f.values as any[])[0] ?? '').slice(0, 20);
                      return `${f.column_name}_${f.filter_type}_${val}`.replace(/[^a-z0-9_]/gi, '_').toLowerCase();
                    }
                    return 'e.g. high_value_customers';
                  })()}
                  value={saveFilterName} onChange={e => setSaveFilterName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())} />
                {saveFilterName && (
                  <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 1 }}>
                    Will appear as: <b>Filtered: {saveFilterName}</b>
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSaveFilterOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button variant="contained" onClick={saveFilteredTable} disabled={!saveFilterName.trim() || savingFilter}
                  sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', '&:hover': { bgcolor: '#2D2D2D' } }}>
                  {savingFilter ? 'Saving…' : 'Save'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* View SQL dialog */}
            <Dialog open={viewSqlOpen} onClose={() => setViewSqlOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Generated SQL
                <IconButton size="small" onClick={() => setViewSqlOpen(false)}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ bgcolor: '#1E1E1E', color: '#D4D4D4', p: 2.5, borderRadius: '8px', fontFamily: '"Fira Code", "Cascadia Code", monospace', fontSize: '0.82rem', lineHeight: 1.7, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {viewSqlText}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { navigator.clipboard.writeText(viewSqlText); }} sx={{ textTransform: 'none', fontSize: '0.8rem', color: '#374151' }}>
                  Copy to clipboard
                </Button>
                <Button onClick={() => setViewSqlOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* ═════════════ ADD COLUMN TAB ═════════════ */}
        {tab === 'add' && (
          <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 820 }}>
            <SectionHeader title="Add a new column" subtitle="Describe the new column in plain English. AI will write the formula for you." />

            <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 3 }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>1. What should the new column be called?</Typography>
              <TextField fullWidth size="small" placeholder="e.g. profit_margin"
                value={addName} onChange={e => setAddName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB' } }} />

              <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>2. Describe how to calculate it</Typography>
              <TextField fullWidth multiline minRows={3} size="small"
                placeholder="e.g. Revenue minus cost, divided by revenue, as a percentage"
                value={addDesc} onChange={e => setAddDesc(e.target.value)}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB' } }} />

              <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '8px', mb: 2, border: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>Examples</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.7 }}>
                  • <i>"Mark orders as High if sales is greater than 1000, else Low"</i><br />
                  • <i>"Extract the year from the date column"</i><br />
                  • <i>"Combine first_name and last_name into full_name"</i><br />
                  • <i>"Profit as revenue minus cost"</i>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="outlined" onClick={previewAddColumn} disabled={!addName.trim() || !addDesc.trim() || addBusy}
                  sx={{ textTransform: 'none', borderColor: '#D1D5DB', color: '#374151' }}>
                  {addBusy && !addPreview ? 'Generating…' : 'Preview'}
                </Button>
                {addPreview && (
                  <Button variant="contained" onClick={applyAddColumn} disabled={addBusy}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2D2D2D' } }}>
                    {addBusy ? 'Adding column…' : 'Add this column'}
                  </Button>
                )}
              </Box>

              {/* Preview explanation */}
              {addPreview && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>AI will do this:</Typography>
                  <Typography sx={{ fontSize: '0.825rem', color: '#111', lineHeight: 1.6 }}>
                    {addPreview.explanation}
                  </Typography>
                </Box>
              )}

              {/* Result */}
              {addResult && (
                <Alert severity={addResult.success ? 'success' : 'error'} sx={{ mt: 2 }} onClose={() => setAddResult(null)}>
                  {addResult.message}
                </Alert>
              )}
            </Box>
          </Box>
        )}

        {/* ═════════════ CHART TAB ═════════════ */}
        {tab === 'chart' && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <SectionHeader title="Build a chart" subtitle="Pick your axes and chart style" />

            {/* Controls */}
            <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 1.5, md: 2.5 }, mb: 3 }}>
              <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>Group by (X axis)</Typography>
                  <Select size="small" value={chartX} onChange={e => { setChartX(e.target.value); setChartHidden(new Set()); }}
                    sx={{ minWidth: { xs: '100%', sm: 180 }, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                    {allColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                  </Select>
                </Box>
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>Measure (Y axis)</Typography>
                  <Select size="small" value={chartY} onChange={e => setChartY(e.target.value)}
                    sx={{ minWidth: { xs: '100%', sm: 180 }, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                    {allColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                  </Select>
                </Box>
                <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#6B7280', mb: 0.5 }}>Combine using</Typography>
                  <Select size="small" value={chartAgg} onChange={e => setChartAgg(e.target.value as any)}
                    sx={{ minWidth: 130, bgcolor: '#F9FAFB', '& .MuiSelect-select': { py: 1, fontSize: '0.85rem' } }}>
                    <MenuItem value="sum" sx={{ fontSize: '0.85rem' }}>Total (sum)</MenuItem>
                    <MenuItem value="mean" sx={{ fontSize: '0.85rem' }}>Average</MenuItem>
                    <MenuItem value="median" sx={{ fontSize: '0.85rem' }}>Middle (median)</MenuItem>
                    <MenuItem value="count" sx={{ fontSize: '0.85rem' }}>Count of rows</MenuItem>
                  </Select>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.5, bgcolor: '#F3F4F6', borderRadius: '8px', p: 0.4, flexWrap: 'wrap' }}>
                  {(['bar', 'line', 'area', 'pie', 'scatter', 'histogram', 'waterfall', 'bubble'] as const).map(t => (
                    <Button key={t} size="small" onClick={() => { setChartType(t); setChartHidden(new Set()); }}
                      sx={{ textTransform: 'capitalize', fontSize: '0.72rem', fontWeight: chartType === t ? 700 : 500, px: 1.25, py: 0.5, borderRadius: '6px', minWidth: 'auto',
                        bgcolor: chartType === t ? '#fff' : 'transparent', color: chartType === t ? '#111' : '#6B7280',
                        boxShadow: chartType === t ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                        '&:hover': { bgcolor: chartType === t ? '#fff' : '#E5E7EB' } }}>{t}</Button>
                  ))}
                </Box>
                <Button
                  size="small"
                  startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                  onClick={() => {
                    const Plotly = (window as any).Plotly;
                    if (Plotly && plotDivRef.current) {
                      Plotly.downloadImage(plotDivRef.current, {
                        format: 'png',
                        filename: `${dataset.name}_${chartX}_by_${chartY}`.replace(/[^a-z0-9_]/gi, '_'),
                        width: 1400, height: 700, scale: 2,
                      });
                    }
                  }}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600, color: '#374151', borderColor: '#E5E7EB', border: '1px solid #E5E7EB', px: 1.5, py: 0.6, '&:hover': { bgcolor: '#F9FAFB' } }}>
                  Download PNG
                </Button>
              </Box>
            </Box>

            {/* Chart + legend chips for pie */}
            {(() => {
              const vc = visibleChart || { labels: [] as string[], values: [] as number[] };
              const showChart = (chartType === 'histogram' && previewRows.length > 0) || (visibleChart && visibleChart.labels.length > 0);
              return showChart ? (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '2 1 500px', bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2 }}>
                  <Plot
                    data={[
                      chartType === 'pie'
                        ? {
                            type: 'pie', labels: vc.labels, values: vc.values,
                            marker: { colors: PALETTE },
                            hole: 0.55,
                            textinfo: 'label+percent',
                            textfont: { family: 'Inter, sans-serif', size: 12 },
                            hoverinfo: 'label+value+percent',
                            sort: false,
                          }
                        : chartType === 'scatter'
                        ? {
                            type: 'scatter', mode: 'markers', x: vc.labels, y: vc.values,
                            marker: { color: PALETTE[0], size: 14, line: { color: '#fff', width: 1.5 } },
                          }
                        : chartType === 'line'
                        ? {
                            type: 'scatter', mode: 'lines+markers', x: vc.labels, y: vc.values,
                            line: { color: PALETTE[1], width: 3, shape: 'spline' },
                            marker: { color: PALETTE[1], size: 7 },
                            fill: 'none',
                          }
                        : chartType === 'area'
                        ? {
                            type: 'scatter', mode: 'lines', x: vc.labels, y: vc.values,
                            line: { color: PALETTE[2], width: 2, shape: 'spline' },
                            fill: 'tozeroy', fillcolor: PALETTE[2] + '55',
                          }
                        : chartType === 'histogram'
                        ? {
                            type: 'histogram' as any,
                            x: previewRows.map(r => Number(r[chartY])).filter(v => !isNaN(v)),
                            marker: { color: PALETTE[3], line: { color: '#fff', width: 1 } },
                            nbinsx: 20,
                            name: humanize(chartY),
                          }
                        : chartType === 'waterfall'
                        ? {
                            type: 'waterfall' as any,
                            x: vc.labels,
                            y: vc.values,
                            connector: { line: { color: '#E5E7EB', width: 1.5 } },
                            increasing: { marker: { color: '#A8D5BA' } },
                            decreasing: { marker: { color: '#F4A5A5' } },
                            totals: { marker: { color: PALETTE[1] } },
                            textposition: 'outside',
                          }
                        : chartType === 'bubble'
                        ? {
                            type: 'scatter', mode: 'markers',
                            x: vc.labels,
                            y: vc.values,
                            marker: {
                              size: vc.values.map(v => Math.max(8, Math.min(60, Math.sqrt(Math.abs(v)) * 3))),
                              color: vc.values,
                              colorscale: 'Portland',
                              showscale: true,
                              line: { color: '#fff', width: 1.5 },
                            },
                            text: vc.labels.map((l, i) => `${l}: ${formatCompact(vc.values[i])}`),
                            hoverinfo: 'text',
                          }
                        : {
                            type: 'bar', x: vc.labels, y: vc.values,
                            marker: { color: vc.labels.map((_, i) => PALETTE[i % PALETTE.length]), line: { color: '#fff', width: 1.5 } },
                          },
                    ] as any}
                    layout={{
                      autosize: true,
                      height: 460,
                      margin: { l: 80, r: 20, t: 40, b: chartType === 'pie' ? 20 : 100 },
                      font: { family: 'Inter, sans-serif', size: 12, color: '#111' },
                      paper_bgcolor: '#fff',
                      plot_bgcolor: '#fff',
                      showlegend: false,
                      title: chartType !== 'pie' ? {
                        text: chartType === 'histogram'
                          ? `<b>Distribution of ${humanize(chartY)}</b>`
                          : `<b>${humanize(chartY)}</b> by <b>${humanize(chartX)}</b> <span style="color:#9CA3AF;font-weight:400;">(${chartAgg === 'sum' ? 'total' : chartAgg === 'mean' ? 'average' : chartAgg === 'median' ? 'median' : 'count'})</span>`,
                        font: { size: 14, color: '#111', family: 'Inter, sans-serif' },
                        x: 0, xanchor: 'left',
                      } as any : undefined,
                      xaxis: chartType !== 'pie' ? {
                        title: { text: humanize(chartX), font: { size: 12, color: '#374151', family: 'Inter, sans-serif' }, standoff: 15 } as any,
                        tickangle: -30, gridcolor: '#F3F4F6', linecolor: '#E5E7EB',
                        tickfont: { size: 11, color: '#6B7280' },
                      } : undefined,
                      yaxis: chartType !== 'pie' ? {
                        title: { text: `${humanize(chartY)} (${chartAgg === 'sum' ? 'total' : chartAgg === 'mean' ? 'avg' : chartAgg === 'median' ? 'median' : 'count'})`, font: { size: 12, color: '#374151', family: 'Inter, sans-serif' }, standoff: 15 } as any,
                        gridcolor: '#F3F4F6', linecolor: '#E5E7EB',
                        tickfont: { size: 11, color: '#6B7280' },
                        tickformat: yType === 'currency' ? '$,.0f' : yType === 'percent' ? '.1%' : ',',
                      } : undefined,
                      transition: { duration: 700, easing: 'cubic-in-out' } as any,
                    }}
                    config={{ displayModeBar: false, responsive: true }}
                    useResizeHandler
                    style={{ width: '100%' }}
                    onInitialized={(_, gd) => { plotDivRef.current = gd; }}
                    onUpdate={(_, gd) => { plotDivRef.current = gd; }}
                  />
                </Box>

                {/* Legend / toggle side panel */}
                <Box sx={{ flex: '1 1 220px', minWidth: 220, bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: 2, maxHeight: 500, overflow: 'auto' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.25 }}>
                    {chartType === 'pie' ? 'Click to hide' : 'Categories'}
                  </Typography>
                  {chartResult?.labels.map((label, i) => {
                    const isHidden = chartHidden.has(label);
                    const color = PALETTE[i % PALETTE.length];
                    return (
                      <Box key={label} onClick={() => toggleChartCat(label)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, borderRadius: '6px',
                          cursor: 'pointer', opacity: isHidden ? 0.35 : 1, transition: 'all 0.2s ease',
                          '&:hover': { bgcolor: '#F9FAFB' },
                        }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: isHidden ? '#9CA3AF' : '#111', flex: 1, textDecoration: isHidden ? 'line-through' : 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: 600 }}>
                          {formatCompact(chartResult.values[i])}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 6, textAlign: 'center', border: '1px dashed #D1D5DB', borderRadius: '12px', bgcolor: '#fff' }}>
                <BarChartIcon sx={{ fontSize: 36, color: '#D1D5DB', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>Pick columns above to build a chart.</Typography>
              </Box>
            );
            })()}
          </Box>
        )}

        {/* ═════════════ AI TAB ═════════════ */}
        {tab === 'ai' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Chat messages */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 1.5, md: 3 } }}>
              <Box sx={{ maxWidth: 820, mx: 'auto' }}>
                {aiMessages.map((m, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 2 }}>
                    <Box sx={{
                      maxWidth: '85%',
                      bgcolor: m.role === 'user' ? '#111' : '#fff',
                      color: m.role === 'user' ? '#fff' : '#111',
                      border: m.role === 'user' ? 'none' : '1px solid #E5E7EB',
                      borderRadius: '14px',
                      px: 2, py: 1.5,
                    }}>
                      {m.loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={14} sx={{ color: '#6B7280' }} />
                          <Typography sx={{ fontSize: '0.85rem', color: '#6B7280' }}>Analyzing your data…</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', lineHeight: 1.6, color: m.role === 'user' ? '#fff' : '#111' }}>
                            {m.text}
                          </Typography>
                          {m.insights && m.insights.length > 0 && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F3F4F6' }}>
                              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>Key Insights</Typography>
                              {m.insights.slice(0, 4).map((ins, k) => (
                                <Typography key={k} sx={{ fontSize: '0.8rem', color: '#374151', mb: 0.4, pl: 1.25, borderLeft: '2px solid #A5C8F4' }}>
                                  {ins}
                                </Typography>
                              ))}
                            </Box>
                          )}
                          {m.data && Array.isArray(m.data) && m.data.length > 0 && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #F3F4F6', overflowX: 'auto' }}>
                              <Box sx={{ display: 'inline-block', minWidth: '100%' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Object.keys(m.data[0]).length}, minmax(100px, 1fr))`, bgcolor: '#F9FAFB', borderRadius: '6px 6px 0 0', borderBottom: '1px solid #E5E7EB' }}>
                                  {Object.keys(m.data[0]).map(k => (
                                    <Box key={k} sx={{ px: 1.25, py: 0.75 }}>
                                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280', whiteSpace: 'nowrap' }}>
                                        {k.replace(/_/g, ' ')}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                                {m.data.slice(0, 10).map((row, j) => (
                                  <Box key={j} sx={{ display: 'grid', gridTemplateColumns: `repeat(${Object.keys(m.data![0]).length}, minmax(100px, 1fr))`, borderBottom: '1px solid #F3F4F6' }}>
                                    {Object.entries(row).map(([k, v]) => (
                                      <Box key={k} sx={{ px: 1.25, py: 0.75 }}>
                                        <Typography sx={{ fontSize: '0.78rem', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {autoFormat(v, k)}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
                <div ref={aiEndRef} />
              </Box>
            </Box>

            {/* Suggested prompts */}
            {aiMessages.length <= 1 && (
              <Box sx={{ px: 3, pb: 1.5 }}>
                <Box sx={{ maxWidth: 820, mx: 'auto' }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>Try asking…</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {suggestedPrompts.map((p, i) => (
                      <Chip key={i} label={p} onClick={() => setAiInput(p)}
                        sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#111' } }} />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Input */}
            <Box sx={{ px: 3, pb: 3, bgcolor: '#F7F8FA' }}>
              <Box sx={{ maxWidth: 820, mx: 'auto', bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '14px', p: 1, display: 'flex', alignItems: 'center', gap: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <TextField fullWidth variant="standard" placeholder="Ask a question about your data…"
                  value={aiInput} onChange={e => setAiInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendAi(); } }}
                  disabled={aiBusy}
                  InputProps={{ disableUnderline: true, sx: { px: 1.5, py: 0.75, fontSize: '0.925rem', color: '#111' } }}
                />
                <IconButton onClick={sendAi} disabled={!aiInput.trim() || aiBusy}
                  sx={{ bgcolor: aiInput.trim() ? '#111' : '#E5E7EB', color: '#fff', '&:hover': { bgcolor: aiInput.trim() ? '#2D2D2D' : '#E5E7EB' }, '&.Mui-disabled': { color: '#9CA3AF' } }}>
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* ═════════════ COLUMNS (IGNORE) TAB ═════════════ */}
        {tab === 'columns' && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <SectionHeader
              title="Manage Columns"
              subtitle="Choose which columns to include in analysis. Ignored columns won't appear in charts, filters, insights, or AI."
            />

            {/* Quick actions bar */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button size="small" variant="outlined"
                onClick={() => setIgnoredColumns(new Set())}
                startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#E5E7EB', color: '#374151', borderRadius: '8px', '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' } }}>
                Include All
              </Button>
              <Button size="small" variant="outlined"
                onClick={() => {
                  const totalRows = overview?.total_rows || previewRows.length;
                  const ids = columnsInfo.filter(c => isIdentifierColumn(c, totalRows)).map(c => c.column_name);
                  setIgnoredColumns(new Set(ids));
                }}
                startIcon={<VisibilityOffIcon sx={{ fontSize: 15 }} />}
                sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: '#E5E7EB', color: '#374151', borderRadius: '8px', '&:hover': { borderColor: '#D1D5DB', bgcolor: '#F9FAFB' } }}>
                Auto-ignore IDs
              </Button>
              {ignoredColumns.size > 0 && (
                <Chip
                  size="small"
                  label={`${ignoredColumns.size} column${ignoredColumns.size > 1 ? 's' : ''} ignored`}
                  onDelete={() => setIgnoredColumns(new Set())}
                  sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: '0.72rem', height: 24, '& .MuiChip-deleteIcon': { color: '#DC2626', fontSize: 16 } }}
                />
              )}
              <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', ml: 'auto' }}>
                {columnsInfo.length - ignoredColumns.size} of {columnsInfo.length} active
              </Typography>
            </Box>

            {/* Column grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.5 }}>
              {columnsInfo.map(col => {
                const isIgnored = ignoredColumns.has(col.column_name);
                const type = col.data_type === 'numeric' ? 'Number' : 'Text';
                const typeColor = col.data_type === 'numeric' ? '#8B5CF6' : '#3B82F6';
                const totalRows = overview?.total_rows || previewRows.length;
                const isId = isIdentifierColumn(col, totalRows);

                const toggle = () => {
                  setIgnoredColumns(prev => {
                    const next = new Set(prev);
                    if (next.has(col.column_name)) {
                      next.delete(col.column_name);
                    } else {
                      next.add(col.column_name);
                    }
                    return next;
                  });
                };

                return (
                  <Box
                    key={col.column_name}
                    onClick={toggle}
                    sx={{
                      border: isIgnored ? '1px solid #FCA5A5' : '1px solid #E5E7EB',
                      borderRadius: '10px',
                      p: 2,
                      bgcolor: isIgnored ? '#FEF2F2' : '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: isIgnored ? '#F87171' : '#A5B4FC',
                        bgcolor: isIgnored ? '#FEE2E2' : '#F5F3FF',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                      },
                    }}
                  >
                    {/* Checkbox indicator */}
                    <Box sx={{ pt: 0.25, flexShrink: 0 }}>
                      {isIgnored
                        ? <CheckBoxOutlineBlankIcon sx={{ fontSize: 22, color: '#D1D5DB' }} />
                        : <CheckBoxIcon sx={{ fontSize: 22, color: '#10B981' }} />
                      }
                    </Box>

                    {/* Column info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography sx={{
                          fontSize: '0.875rem', fontWeight: 600,
                          color: isIgnored ? '#9CA3AF' : '#111',
                          textDecoration: isIgnored ? 'line-through' : 'none',
                          lineHeight: 1.3,
                        }}>
                          {humanize(col.column_name)}
                        </Typography>
                        <Chip label={type} size="small"
                          sx={{ bgcolor: isIgnored ? '#F3F4F6' : typeColor + '15', color: isIgnored ? '#9CA3AF' : typeColor, fontWeight: 600, fontSize: '0.62rem', height: 16 }} />
                        {isId && !isIgnored && (
                          <Chip label="ID" size="small"
                            sx={{ bgcolor: '#FFFBEB', color: '#B45309', fontWeight: 600, fontSize: '0.6rem', height: 16 }} />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.72rem', color: isIgnored ? '#D1D5DB' : '#6B7280', lineHeight: 1.4 }}>
                        {col.column_name}
                        {col.data_type === 'numeric' && col.min_value != null
                          ? ` · Range: ${autoFormat(col.min_value, col.column_name)} — ${autoFormat(col.max_value, col.column_name)}`
                          : ''}
                        {col.data_type === 'categorical' && col.unique_count != null
                          ? ` · ${col.unique_count} unique values`
                          : ''}
                      </Typography>
                    </Box>

                    {/* Status badge */}
                    <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                      {isIgnored
                        ? <Chip size="small" label="Ignored" icon={<VisibilityOffIcon sx={{ fontSize: 11 }} />}
                            sx={{ bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600, fontSize: '0.62rem', height: 20, '& .MuiChip-icon': { color: '#DC2626', ml: 0.5 } }} />
                        : <Chip size="small" label="Active" icon={<VisibilityIcon sx={{ fontSize: 11 }} />}
                            sx={{ bgcolor: '#F0FDF4', color: '#059669', fontWeight: 600, fontSize: '0.62rem', height: 20, '& .MuiChip-icon': { color: '#059669', ml: 0.5 } }} />
                      }
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Hint footer */}
            <Box sx={{ mt: 3, p: 2, bgcolor: '#F9FAFB', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', lineHeight: 1.6 }}>
                <b style={{ color: '#374151' }}>How this works:</b> Ignored columns are hidden from Chart dropdowns,
                Filter options, Smart Insights (correlations, outliers, recommendations), and AI queries.
                The raw data is still there — you can re-include any column at any time. This is useful for
                excluding ID fields, timestamps, or any columns that aren't relevant to your analysis.
              </Typography>
            </Box>
          </Box>
        )}

        {/* ═════════════ WRANGLE TAB ═════════════ */}
        {tab === 'wrangle' && (
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <SectionHeader
              title="Wrangle your data"
              subtitle="Excel-style tools: join tables, remove duplicates, sort, and extract unique values"
            />

            {/* Tool selector */}
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {([
                { id: 'join',       label: 'Join / VLOOKUP',    icon: <MergeTypeIcon sx={{ fontSize: 15 }} />,          desc: 'Combine two datasets on a shared column' },
                { id: 'dedup',      label: 'Remove Duplicates', icon: <DeduplicateIcon sx={{ fontSize: 15 }} />,        desc: 'Keep only unique rows' },
                { id: 'duplicates', label: 'Find Duplicates',   icon: <FormatListNumberedIcon sx={{ fontSize: 15 }} />, desc: 'Show all repeated rows' },
                { id: 'sort',       label: 'Sort & Rank',       icon: <SortIcon sx={{ fontSize: 15 }} />,               desc: 'Sort dataset by one or more columns' },
                { id: 'unique',     label: 'Unique Values',     icon: <JoinInnerIcon sx={{ fontSize: 15 }} />,          desc: 'Extract distinct values from a column' },
              ] as const).map(tool => (
                <Button key={tool.id} onClick={() => { setWrangleTool(tool.id); setWrangleResult(null); }}
                  startIcon={tool.icon}
                  variant={wrangleTool === tool.id ? 'contained' : 'outlined'}
                  size="small"
                  sx={{
                    textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 2, py: 1, borderRadius: '8px',
                    ...(wrangleTool === tool.id
                      ? { bgcolor: '#111', color: '#fff', borderColor: '#111', '&:hover': { bgcolor: '#2D2D2D' } }
                      : { borderColor: '#E5E7EB', color: '#374151', '&:hover': { bgcolor: '#F9FAFB' } }),
                  }}>
                  {tool.label}
                </Button>
              ))}
            </Box>

            {/* Result banner */}
            {wrangleResult && (
              <Alert severity={wrangleResult.success ? 'success' : 'error'} sx={{ mb: 3 }} onClose={() => setWrangleResult(null)}>
                {wrangleResult.message}
              </Alert>
            )}

            {/* ── JOIN / VLOOKUP ── */}
            {wrangleTool === 'join' && (
              <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MergeTypeIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111' }}>Join / VLOOKUP</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                      Like Excel's VLOOKUP — combine this dataset with another using a shared column (e.g. Customer ID, Product Code).
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>This dataset</Typography>
                    <Box sx={{ px: 2, py: 1.25, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#111' }}>{dataset.name}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{allColumns.length} columns</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Lookup dataset</Typography>
                    <Select size="small" fullWidth displayEmpty value={joinTableB} onChange={e => { setJoinTableB(e.target.value); setJoinColB(''); }}
                      sx={{ bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                      <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Choose a dataset…</MenuItem>
                      {allDatasets.map(d => <MenuItem key={d.table_name} value={d.table_name} sx={{ fontSize: '0.85rem' }}>{d.name}</MenuItem>)}
                    </Select>
                    {allDatasets.length === 0 && <Typography sx={{ fontSize: '0.72rem', color: '#F59E0B', mt: 0.5 }}>Upload another dataset to enable joining.</Typography>}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Match on (this dataset)</Typography>
                    <Select size="small" fullWidth displayEmpty value={joinColA} onChange={e => setJoinColA(e.target.value)}
                      sx={{ bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                      <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Pick column…</MenuItem>
                      {allColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                    </Select>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Match on (lookup dataset)</Typography>
                    <Select size="small" fullWidth displayEmpty value={joinColB} onChange={e => setJoinColB(e.target.value)} disabled={!joinTableB}
                      sx={{ bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                      <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Pick column…</MenuItem>
                      {joinTableBCols.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                    </Select>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Join type</Typography>
                    <Select size="small" fullWidth value={joinType} onChange={e => setJoinType(e.target.value as any)}
                      sx={{ bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                      <MenuItem value="left" sx={{ fontSize: '0.85rem' }}>Left — keep all rows from this dataset</MenuItem>
                      <MenuItem value="inner" sx={{ fontSize: '0.85rem' }}>Inner — only rows that match in both</MenuItem>
                      <MenuItem value="right" sx={{ fontSize: '0.85rem' }}>Right — keep all rows from lookup</MenuItem>
                      <MenuItem value="full" sx={{ fontSize: '0.85rem' }}>Full — keep all rows from both</MenuItem>
                    </Select>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Save result as</Typography>
                    <TextField size="small" fullWidth placeholder="e.g. orders_with_customers"
                      value={joinNewName} onChange={e => setJoinNewName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB', fontSize: '0.85rem' } }} />
                  </Box>
                  <Button variant="contained" onClick={runJoin} disabled={wrangleBusy || !joinTableB || !joinColA || !joinColB || !joinNewName}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, py: 1.1, '&:hover': { bgcolor: '#2D2D2D' } }}>
                    {wrangleBusy ? 'Joining…' : 'Run Join'}
                  </Button>
                </Box>

                <Box sx={{ mt: 2.5, p: 2, bgcolor: '#F0F9FF', borderRadius: '8px', border: '1px solid #BAE6FD' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#0369A1', lineHeight: 1.6 }}>
                    <b>VLOOKUP equivalent:</b> Select "Left" join type to replicate Excel's VLOOKUP — every row from this dataset stays, and matching values from the lookup table are added as new columns. Rows with no match get blank values.
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ── REMOVE DUPLICATES ── */}
            {wrangleTool === 'dedup' && (
              <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <DeduplicateIcon sx={{ fontSize: 18, color: '#10B981' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111' }}>Remove Duplicates</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                      Like Excel's "Remove Duplicates" — choose which columns define a duplicate, then keep only one copy.
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>
                  Which columns define a duplicate? <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(leave blank to use all columns)</span>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5, p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                  {allColumns.map(col => {
                    const checked = dedupCols.includes(col);
                    return (
                      <Chip key={col} label={col} size="small" clickable
                        onClick={() => setDedupCols(prev => checked ? prev.filter(c => c !== col) : [...prev, col])}
                        variant={checked ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.75rem', fontWeight: checked ? 600 : 400, bgcolor: checked ? '#111' : 'transparent', color: checked ? '#fff' : '#374151', borderColor: checked ? '#111' : '#E5E7EB' }}
                      />
                    );
                  })}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>When duplicates exist, keep</Typography>
                    <Select size="small" value={dedupKeep} onChange={e => setDedupKeep(e.target.value as any)}
                      sx={{ minWidth: 220, bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                      <MenuItem value="first" sx={{ fontSize: '0.85rem' }}>First occurrence</MenuItem>
                      <MenuItem value="last" sx={{ fontSize: '0.85rem' }}>Last occurrence</MenuItem>
                      <MenuItem value="none" sx={{ fontSize: '0.85rem' }}>Remove all copies (keep only 100% unique)</MenuItem>
                    </Select>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Save result as</Typography>
                    <TextField size="small" fullWidth placeholder="e.g. customers_deduped"
                      value={dedupNewName} onChange={e => setDedupNewName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB', fontSize: '0.85rem' } }} />
                  </Box>
                  <Button variant="contained" onClick={runDedup} disabled={wrangleBusy || !dedupNewName}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, py: 1.1, '&:hover': { bgcolor: '#2D2D2D' } }}>
                    {wrangleBusy ? 'Processing…' : 'Remove Duplicates'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── FIND DUPLICATES ── */}
            {wrangleTool === 'duplicates' && (
              <Box>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 }, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FormatListNumberedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111' }}>Find Duplicates</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                        Shows all rows that appear more than once — based on the columns you choose.
                      </Typography>
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1 }}>Check for duplicates across:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5, p: 1.5, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    {allColumns.map(col => {
                      const checked = dupCheckCols.includes(col);
                      return (
                        <Chip key={col} label={col} size="small" clickable
                          onClick={() => setDupCheckCols(prev => checked ? prev.filter(c => c !== col) : [...prev, col])}
                          variant={checked ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.75rem', fontWeight: checked ? 600 : 400, bgcolor: checked ? '#F59E0B' : 'transparent', color: checked ? '#fff' : '#374151', borderColor: checked ? '#F59E0B' : '#E5E7EB' }}
                        />
                      );
                    })}
                  </Box>

                  <Button variant="contained" onClick={runFindDuplicates} disabled={wrangleBusy || dupCheckCols.length === 0}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, py: 1.1, '&:hover': { bgcolor: '#2D2D2D' } }}>
                    {wrangleBusy ? 'Scanning…' : 'Find Duplicates'}
                  </Button>
                </Box>

                {dupRows.length > 0 && (
                  <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#FFFBEB', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningAmberIcon sx={{ fontSize: 16, color: '#F59E0B' }} />
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#92400E' }}>{dupRows.length} duplicate rows found</Typography>
                    </Box>
                    <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${allColumns.length + 1}, minmax(130px, 1fr))`, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0 }}>
                        <Box sx={{ px: 2, py: 1 }}><Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase' }}>Count</Typography></Box>
                        {allColumns.map(col => <Box key={col} sx={{ px: 2, py: 1 }}><Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{col.replace(/_/g, ' ')}</Typography></Box>)}
                      </Box>
                      {dupRows.map((row, i) => (
                        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: `repeat(${allColumns.length + 1}, minmax(130px, 1fr))`, borderBottom: '1px solid #F3F4F6', bgcolor: i % 2 === 0 ? '#FFFBEB22' : 'transparent' }}>
                          <Box sx={{ px: 2, py: 1 }}><Chip size="small" label={row.duplicate_count} sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700, height: 18, fontSize: '0.7rem' }} /></Box>
                          {allColumns.map(col => <Box key={col} sx={{ px: 2, py: 1 }}><Typography sx={{ fontSize: '0.78rem', color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{autoFormat(row[col], col)}</Typography></Box>)}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* ── SORT & RANK ── */}
            {wrangleTool === 'sort' && (
              <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#F5F3FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <SortIcon sx={{ fontSize: 18, color: '#8B5CF6' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111' }}>Sort & Rank</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                      Sort this dataset by one or more columns and save as a new dataset you can analyze.
                    </Typography>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 1.5 }}>Sort by:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                  {sortCols.map((s, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Select size="small" displayEmpty value={s.column}
                        onChange={e => setSortCols(prev => prev.map((x, j) => j === i ? { ...x, column: e.target.value } : x))}
                        sx={{ minWidth: { xs: '100%', sm: 220 }, bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                        <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Pick column…</MenuItem>
                        {allColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                      </Select>
                      <Select size="small" value={s.direction}
                        onChange={e => setSortCols(prev => prev.map((x, j) => j === i ? { ...x, direction: e.target.value as 'asc' | 'desc' } : x))}
                        sx={{ minWidth: 150, bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                        <MenuItem value="asc" sx={{ fontSize: '0.85rem' }}>A → Z (ascending)</MenuItem>
                        <MenuItem value="desc" sx={{ fontSize: '0.85rem' }}>Z → A (descending)</MenuItem>
                      </Select>
                      {sortCols.length > 1 && (
                        <IconButton size="small" onClick={() => setSortCols(prev => prev.filter((_, j) => j !== i))} sx={{ color: '#9CA3AF', '&:hover': { color: '#EF4444' } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  <Button size="small" onClick={() => setSortCols(prev => [...prev, { column: '', direction: 'asc' }])}
                    sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#6B7280', alignSelf: 'flex-start', pl: 0 }}>
                    + Add another sort column
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Save result as</Typography>
                    <TextField size="small" fullWidth placeholder="e.g. orders_by_date"
                      value={sortNewName} onChange={e => setSortNewName(e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
                      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#F9FAFB', fontSize: '0.85rem' } }} />
                  </Box>
                  <Button variant="contained" onClick={runSort} disabled={wrangleBusy || !sortCols.some(s => s.column) || !sortNewName}
                    sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, py: 1.1, '&:hover': { bgcolor: '#2D2D2D' } }}>
                    {wrangleBusy ? 'Sorting…' : 'Sort & Save'}
                  </Button>
                </Box>
              </Box>
            )}

            {/* ── UNIQUE VALUES ── */}
            {wrangleTool === 'unique' && (
              <Box>
                <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', p: { xs: 2, md: 3 }, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '8px', bgcolor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <JoinInnerIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#111' }}>Unique Values</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: '#6B7280' }}>
                        See every distinct value in a column with its frequency — like Excel's "Remove Duplicates" preview or a Pivot Table count.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Pick a column</Typography>
                      <Select size="small" fullWidth displayEmpty value={uniqueCol} onChange={e => setUniqueCol(e.target.value)}
                        sx={{ bgcolor: '#F9FAFB', '& .MuiSelect-select': { fontSize: '0.85rem', py: 1 } }}>
                        <MenuItem value="" disabled sx={{ fontSize: '0.85rem', color: '#9CA3AF' }}>Choose column…</MenuItem>
                        {allColumns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>{c}</MenuItem>)}
                      </Select>
                    </Box>
                    <Button variant="contained" onClick={runUniqueValues} disabled={wrangleBusy || !uniqueCol}
                      sx={{ bgcolor: '#111', color: '#fff', textTransform: 'none', fontWeight: 600, px: 3, py: 1.1, '&:hover': { bgcolor: '#2D2D2D' } }}>
                      {wrangleBusy ? 'Loading…' : 'Show Unique Values'}
                    </Button>
                  </Box>
                </Box>

                {uniqueRows.length > 0 && (
                  <Box sx={{ bgcolor: '#fff', border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>
                        {uniqueRows.length} unique values in <b>"{uniqueCol}"</b>
                      </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
                      {uniqueRows.map((row, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.25, borderBottom: '1px solid #F3F4F6', gap: 2 }}>
                          <Typography sx={{ fontSize: '0.85rem', color: '#111', flex: 1, fontWeight: 500 }}>{String(row[uniqueCol] ?? '—')}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* Mini bar */}
                            <Box sx={{ width: 80, height: 6, bgcolor: '#F3F4F6', borderRadius: 9999, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${row.percentage}%`, bgcolor: '#A8D5BA', borderRadius: 9999 }} />
                            </Box>
                            <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                              {row.percentage}%
                            </Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF', minWidth: 55, textAlign: 'right' }}>
                              {formatInteger(row.frequency)} rows
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExplorePage;
